/**
 * Programme detail content, imported from the previous A Learning Lab website.
 *
 * Source: the 13 writer-listed virtual internship pages on alearninglab.com (see `sourceUrl`). Copy is kept as written
 * by the content team; images still point at the old site's uploads and should be re-hosted later.
 *
 * Notes for editors
 * - `batches[].start` (ISO date) is used to hide batches that have already started.
 * - `fee` and `batches` are what the old site showed; confirm them against the backend before launch.
 * - `partnership` is only kept where the old copy was correct for that programme.
 * - SEO fields (`metaTitle`, `metaDescription`) are first drafts for the SEO specialist.
 *
 * Programme card thumbnails come from `getProgramCardImage(slug)` (see src/lib/programs.js).
 */

export const PROGRAM_DETAILS = [
  {
    "slug": "medicine-and-healthcare-virtual-internship",
    "industry": "health",
    "title": "Medicine & Healthcare Virtual Internship",
    "metaTitle": "Medicine & Healthcare Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Step into the world of medicine through clinical scenarios, shadowing doctors, and developing skills in patient care, clinical… Live online, ages 13-18.",
    "summary": "Step into the world of medicine through clinical scenarios, shadowing doctors, and developing skills in patient care, clinical reasoning, and understanding how real healthcare systems function.",
    "subtitle": "From Diagnosis to Decision-Making: Experience Medicine & Healthcare",
    "lead": "6-Week Guided Virtual Shadowing for Ages 13–18",
    "promise": "Turn Your Passion for Medicine into Real Impact",
    "perks": [
      "No prior medical experience required",
      "Live sessions every Saturday",
      "Shadow real doctors and healthcare professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=258",
    "sourceUrl": "https://alearninglab.com/medicine-virtual-shadowing-program/",
    "mentors": [
      {
        "name": "Dr. Lorna Sharma",
        "bio": "Dr. Lorna is a UK-born, NHS-trained Family Medicine Consultant with 10+ years of experience across emergency medicine, internal medicine, paediatrics, obstetrics & gynaecology, psychiatry, and general surgery. Now based in Dubai and licensed by the Dubai Health Authority, she brings extensive UK and UAE clinical experience, offering students valuable insight into global healthcare systems, medical specialisms, and real-world clinical practice. Her expertise also spans women’s health, longevity medicine, hormone care, mother-and-baby care, preventative healthcare, and holistic patient-centered treatment.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/05/Frame-1-1024x1024.png"
      },
      {
        "name": "Dr. Miaad",
        "bio": "Dr. Miaad, an MBBS graduate from China Medical University, is a student counselor with experience supporting students studying abroad. With a focus on clear communication, cultural insight, and trust-building, she helps students confidently navigate academic choices and adapt to new environments.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483347.png"
      },
      {
        "name": "Dr. Anjali Gupta",
        "bio": "Dr. Anjali Gupta is a Consultant in Obstetrics, Gynaecology, and Laparoscopic Surgery with extensive experience in women’s health and maternal care. An MBBS Gold Medalist with MRCOG (UK) and other advanced qualifications, she brings strong clinical insight and ethical practice. As a mentor, she guides interns in developing sound clinical judgment and patient-focused care.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Dr.-Anjali-Gupta-Medicine-VI-1.png"
      },
      {
        "name": "Rayan",
        "bio": "Rayan is a medical student and experienced admissions counselor who understands the challenges of medical school firsthand. Drawing from her own journey, she provides practical, empathetic guidance to help aspiring doctors navigate admissions, make informed decisions, and find the path that truly suits them.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483346.png"
      },
      {
        "name": "Varshitaa Prasad",
        "bio": "Varshitaa is a second-year medical student at The University of Manchester. Having successfully navigated the medical school application process and secured offers from all four UK universities she applied to, she brings recent, first-hand experience to her role as a mentor. She is passionate about guiding students through the complexities of medical admissions and is committed to offering practical, honest support throughout their journey.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Varshitaa.png"
      },
      {
        "name": "Dr. Abhimanyu Baruah",
        "bio": "Dr. Abhimanyu is currently practicing medicine in the UK. He graduated from the University of Nottingham after completing his earlier studies in Dubai. Having recently achieved an All-India Rank of 330, Abhi will begin his Internal Medicine Training in London next year. His academic journey and competitive success have equipped him with valuable insights into the demanding path toward medical school and postgraduate training.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/06/Abhimanyu-Baruah.png"
      },
      {
        "name": "Arya Ravi",
        "bio": "Arya Ravi is a third-year MBBS student at RRRMCH, Bangalore, with a focused interest in Cardiothoracic and Vascular Surgery. His clinical exposure spans two years of active postings across departments – observing OT and OPD cases – complemented by a one-month doctor shadowing and observership at NMC Royal Khalifa City, Abu Dhabi, in the Interventional Cardiology department. A consistent academic performer with First Class results across all three years, he has delivered seminars and lectures on clinical and pre-clinical subjects, and participated in hands-on workshops covering CPR, Basic First Aid, suturing techniques, road traffic accident management, and mental health wellness. Arya brings the perspective of a driven, community-conscious medical student with real clinical exposure and a genuine passion for the future of surgery and patient care.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Frame-1707483417.png"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Hands-On Clinical Practice",
        "text": "Participate in role-plays, case studies, and emergency scenarios to experience real-world patient care.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-01.png"
      },
      {
        "title": "Medical Knowledge in Action",
        "text": "Apply anatomy, physiology, and common conditions to assess and prioritize patient care.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-02.png"
      },
      {
        "title": "Problem Solving Acumen",
        "text": "Analyze cases and emergencies to develop clinical reasoning, teamwork, and decision-making skills.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-03.png"
      },
      {
        "title": "Practical Skills Development",
        "text": "Explore medical specialties, hospital workflows, and pathways to study medicine locally and globally.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-04.png"
      },
      {
        "title": "Ethics & Professionalism",
        "text": "Practice empathy, active listening, and ethical decision-making in patient interactions.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-05.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Receive an official certificate from the partner company, helping in strengthening your profile.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Med-06.png"
      }
    ],
    "journeyTitle": "How it Works: Your 6-Week Transformation Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Clinical Practice & Professionalism",
        "text": "Discover what it takes to be a doctor, explore essential skills, ethics, and communication. Understand teamwork in healthcare, patient advocacy, and how doctors balance responsibility, empathy, and decision-making."
      },
      {
        "label": "Week 2",
        "title": "Patient Care & Basic Life Support",
        "text": "Explore hospital departments, patient-centered care, and practice life-saving skills like CPR, AED use, and choking management."
      },
      {
        "label": "Week 3",
        "title": "History Taking & Examination",
        "text": "Master how doctors take patient histories, perform basic exams, and apply preventive care in real-life scenarios."
      },
      {
        "label": "Week 4",
        "title": "Clinical Practice & Case Studies",
        "text": "Analyze common medical cases, learn to assess, prioritize, and manage conditions, and develop clinical reasoning skills."
      },
      {
        "label": "Week 5",
        "title": "Surgical & Emergency Medicine",
        "text": "Experience surgical and emergency medicine with hands-on practice, scenario-based decision-making, and teamwork under pressure."
      },
      {
        "label": "Week 6",
        "title": "Reflection & Medical Careers",
        "text": "Reflect on your learning, explore medical specialties, career paths, and hear insights from practicing doctors."
      }
    ],
    "experience": {
      "title": "Experience Medicine in the Real World",
      "text": "This is more than an online course, it’s a virtual shadowing experience into the ever-present healthcare industry. Your child will engage with real-world patient care, clinical scenarios, and practical insights into how doctors and medical teams work.",
      "points": [
        "Live case studies",
        "Shadow practising doctors",
        "Guest speaker Q&A",
        "Behind-the-scenes hospital insights",
        "Career guidance in medicine",
        "Master critical first-response techniques"
      ]
    },
    "testimonials": [
      {
        "name": "Rwaa Ramadan Elfayoumy",
        "tag": "Virtual Shadowing with NHS Doctors",
        "text": "The explanations done by the mentors were excellent; it made me more aware of how I feel about pursuing a career in medicine. The sessions with the doctor were engaging as she gave as an insight into what her job experience is like and how to deal with patients. I gained a lot of skills in terms of patient communication - how to deliver bad news, verbal and non verbal behaviour towards patient, and how to deal with uncooperative patients - as well as being able to understand the non verbal signs from a patient. In addition to that, I gained further insight into how communication and leadership skills are important in saving a patient’s life. For example, when met with a code blue case, there must be one person who is leading. The mentors explained that statement really well, and explained the other roles in an ACLS. I would like to say thank you to all the mentors, and wish them the best of luck in their career!"
      },
      {
        "name": "Siddhant Pandita",
        "tag": "Virtual Doctor Shadowing",
        "text": "I found the course to be very well composed, with the key ideas of what it means to be a doctor in the NHS clearly and effectively presented. Therefore, I have no specific topic or component that I would like A Learning Lab to add to the curriculum."
      },
      {
        "name": "Laibah",
        "tag": "Virtual Doctor Shadowing",
        "text": "I believe the curriculum is already very comprehensive, as it includes case studies, hypothetical scenarios, and potential patient interactions. There isn’t anything more I would suggest adding since it effectively covers all key aspects of medical shadowing."
      },
      {
        "name": "Angela Eva Biju",
        "tag": "Virtual Shadowing with NHS Doctors",
        "text": "This has been my very first shadowing experience, and it has definitely given me insights into what the field of medicine really looks like. My mentors for the sessions, Miss Moniba and Miss Imaan have been the best at clarifying our queries, whether big or small- and through their demeanor they've created an open atmosphere of discussion and learning. The knowledge they've imparted has definitely impacted me, as I'm always left with something or the other to research and no more about at the end of each session! This program has, no doubt, strengthened the passion I have for medicine and healthcare. Thank you very much!"
      },
      {
        "name": "Reisha Gandhi",
        "tag": "Virtual Internship Medicine",
        "text": "In the Virtual Medical Course, I was able to learn about different specialties and what each of them require. It was a very interesting course and I was able to speak to different Doctors in each field as well."
      },
      {
        "name": "Leen Osman",
        "tag": "Virtual Internship Medicine",
        "text": "I learnt a lot of new information during these sessions and found them to be really enlightening. One thing that was really interesting was getting to learn about all the many different types of specialties as well as some basic skills that are needed in these specialties."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 - 22 October, 2026 (Sundays till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 - 29 October, 2026 (Sundays till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior medical knowledge or clinical experience?",
        "a": "Absolutely not. This programme is designed for beginners with no prior medical knowledge or clinical experience. We start with the fundamentals – understanding how hospitals function, observing patient care, and learning basic medical concepts – and build step by step from there. If your child is curious about medicine and comfortable learning in a professional environment, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual doctor shadowing every Saturday. Students interact directly with medical mentors, observe real clinical workflows, participate in guided discussions, and ask questions in real time. This live, hands-on format is essential – this is active medical exposure, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both. The programme balances individual learning with collaborative group activities. Students independently reflect on clinical observations and case scenarios while also participating in group discussions and guided team-based learning sessions – mirroring how healthcare professionals collaborate in real clinical environments."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a structured, medicine-focused capstone assignment that reflects clinical observation, ethical understanding, and analytical thinking. The certificate issued by the partner company highlights initiative, early healthcare exposure, and participation in a structured medical shadowing experience."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive an industry-recognized certificate upon successful completion of the program. Many use this strong foundation to explore school science clubs, health societies, research initiatives, or advanced coursework in biology and medical sciences. Post-program, students may also enroll in A Learning Lab’s specialized offerings and avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career pathway in medicine and healthcare."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "psychology-and-human-behaviour-virtual-internship",
    "industry": "health",
    "title": "Psychology & Human Behaviour Virtual Internship",
    "metaTitle": "Psychology & Human Behaviour Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Explore the science of human behaviour through real-world scenarios while building skills in observation, empathy, behavioural… Live online, ages 13-18.",
    "summary": "Explore the science of human behaviour through real-world scenarios while building skills in observation, empathy, behavioural analysis, and decision-making to understand how people think, feel, and act.",
    "subtitle": "Discover How the Mind Works: Explore Psychology and Human Behaviour",
    "lead": "Experiential 6-Week Virtual Internship for Ages 13–18",
    "promise": "Dive Into the Science of Thoughts, Emotions, and Behaviour",
    "perks": [
      "No prior psychology experience required",
      "Live sessions every Saturday",
      "Shadow psychologists and mental health professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=257",
    "sourceUrl": "https://alearninglab.com/psychology-virtual-shadowing-program/",
    "mentors": [
      {
        "name": "Areesha Velani",
        "bio": "Areesha is the Director of Admissions at UniHawk with a Bachelor’s degree in Psychology and a minor in Business from Baylor University. With global exposure across the US and UAE, she has supported students in securing admissions to top universities worldwide. She also serves as a Mental Health Lead, mentoring students through psychology-driven academic and personal development.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Areesha-Psychology-VI-2.png"
      },
      {
        "name": "Rashini",
        "bio": "Rashini is a Psychology graduate from the University of Texas at Arlington with minors in Neuroscience and Social Justice & Social Welfare. She has experience in organizational psychology research and works as a Behavioral Therapist in Dallas, Texas. Rashini is passionate about advocacy, community impact, and guiding students exploring academic and career pathways in psychology.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Rashini-Psychology-VI-1.png"
      },
      {
        "name": "Navya Chawla",
        "bio": "Navya Chawla is a Psychology graduate from King’s College London. Her studies focused on social behaviour, cognitive processes, and mental health, with research on how people interpret and respond to complex social situations. An active member of the KCL Psychology Society, she is particularly interested in forensic psychology, behavioural analysis, and psychology’s role within social and legal systems.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/03/Screen-Shot-2026-03-14-at-5.42.19-PM.png"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Hands-On Practice",
        "text": "Engage in role-plays, case studies, and observation activities to experience real-world psychological assessment.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-01.png"
      },
      {
        "title": "Psychology in Action",
        "text": "Apply concepts from clinical, educational, forensic, and sports psychology to understand emotions and behaviour.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-02.png"
      },
      {
        "title": "Strategic Problem Solving",
        "text": "Analyze cases and scenarios to develop reasoning, observation, and decision-making skills.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-03.png"
      },
      {
        "title": "Practical Skills Development",
        "text": "Learn active listening, empathy, counselling, and behaviour analysis for performance and wellbeing.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-04.png"
      },
      {
        "title": "Shadow Psychologists",
        "text": "Explore careers in psychology, shadow professionals, and gain insights into daily workflows and career paths.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-05.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Receive an official certificate from the partner company, helping in strengthening your profile.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/ps-06.png"
      }
    ],
    "journeyTitle": "How it Works: Your 6-Week Transformation Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Foundations of Psychology",
        "text": "Discover what psychology is, explore core concepts like behaviour, cognition, and memory, and learn about diverse psychology careers and professional pathways."
      },
      {
        "label": "Week 2",
        "title": "Clinical Psychology & Counselling Skills",
        "text": "Understand mental health, practice active listening, empathy, and supportive questioning, and explore techniques used by clinical psychologists to help clients."
      },
      {
        "label": "Week 3",
        "title": "Educational Psychology & Learning Support",
        "text": "Learn how psychologists support learning and wellbeing in schools, apply key educational theories, and analyse strategies for behaviour and academic development."
      },
      {
        "label": "Week 4",
        "title": "Forensic Psychology & Criminal Behaviour",
        "text": "Examine crime and cognition, explore offender profiling, eyewitness reliability, and decision-making, and analyse real-world forensic cases."
      },
      {
        "label": "Week 5",
        "title": "Organizational & Industrial Psychology",
        "text": "Understand workplace behaviour, motivation, leadership, and teamwork, practise problem-solving through real-world organizational challenges, and explore how psychologists improve employee performance, wellbeing, and workplace culture."
      },
      {
        "label": "Week 6",
        "title": "Reflection & Careers in Psychology",
        "text": "Reflect on key learnings, create personalised interest maps, explore career pathways, and gain insights from practising psychologists."
      }
    ],
    "experience": {
      "title": "Discover Psychology in Action",
      "text": "This is more than an online course, step into the shoes of a psychologist and explore real-world applications of psychology. Students will engage with real-world scenarios, interactive case studies, and gain practical insights into how psychologists work across clinical, educational, forensic, and sports settings.",
      "points": [
        "Live case studies and role-plays",
        "Shadow practising psychologists",
        "Guest speaker Q&A",
        "Behind-the-scenes insights into psychological practice",
        "Career guidance in psychology",
        "Hands-on practice with counselling & assessment"
      ]
    },
    "testimonials": [
      {
        "name": "Muhammad Aniq Arsyad",
        "tag": "Virtual Psychologist Shadowing Program",
        "text": "I participated in virtual psychologist shadowing programme from 26th July to August 30th every Saturday. Initially, I was unsure of what the programme was about and was recommended to join it from my school in Dubai. When coming to the classes I barely had any previous knowledge in Psychology at a GCSE level. I have learnt a range of subtopics in Psychology especially in Philosphy, clinical psycholgy and foresnsics. This has really helped me to broaden my scope on which profession I should consider when I enter medical school! I loved the interactive activities we did especially the debates. Thank you so much for this experience!"
      },
      {
        "name": "Lamees Arthold",
        "tag": "Virtual Psychologist Shadowing Program",
        "text": "I loved the course, it has really helped me deepen my knowledge in psychology and even a little philosophy! Each course day was super interactive with debates, short answer questions after a lecture and prompts to discuss about each topic. The content was very well structured, I appreciated that we were taught about the different pathways a degree in psychology would take us and then deepens our knowledge into specific areas, psychologists and theories. My mentor was amazing, he was super passionate about each area we covered and was super supportive whenever a question was asked. His passion made the course that much more enjoyable and the content he was sharing that much more interesting."
      },
      {
        "name": "Gursharnpreet Kaur",
        "tag": "Virtual Psychologist Shadowing Program",
        "text": "Ms Ayesha was a great psychologist and I learnt a lot from her. I was also able to hear debates from various perspectives which really shows how people see things in a different way. It was a great experience."
      },
      {
        "name": "Izma Noman",
        "tag": "Virtual Psychologist Shadowing Program",
        "text": "The course was very enjoyable and I feel I was able to learn a lot about the different fields and aspects of psychology. The mentor was also very nice, including us and making sure we all understood what he was talking about."
      },
      {
        "name": "Aniket Rohitkumar",
        "tag": "Virtual Psychologist Shadowing Program",
        "text": "The psychology course was very good, I liked the intros into different fields and concepts. The mentor was very interactive and made us do debates and actual psychological surveys which I found very interesting. It was all round a great experience."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sunday till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sunday till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior knowledge or experience in psychology to participate?",
        "a": "Absolutely not! This program is designed specifically for beginners with no prior knowledge of psychology. We start with the fundamentals – basic concepts, research methods, and how psychological ideas apply to real-life situations – and build step by step from there. If your child is curious about human behavior and enjoys learning, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual shadowing every Saturday. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on activities, and ask questions in real time. The live, hands-on format is essential – this is active learning and shadowing in psychology, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual learning with collaborative activities. Students work on their own psychology mini-project or research idea while also participating in team exercises and group discussions. This mirrors how real research and psychology teams collaborate in professional settings."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a psychology-focused project that demonstrates real analytical and critical thinking skills. The certificate issued reflects their initiative, hands-on learning, and exposure to practical psychological concepts."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate upon successful completion of the internship. Many use this strong foundation to explore school psychology clubs, participate in research initiatives, enter science or behavioral competitions, or take advanced psychology coursework. After the internship, students also have the opportunity to enroll in A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey in psychology and related fields."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "healthtech-virtual-internship",
    "industry": "health",
    "title": "Healthtech Virtual Internship",
    "metaTitle": "Healthtech Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Discover how technology is transforming healthcare by analyzing data, designing solutions, and building skills in… Live online, ages 13-18.",
    "summary": "Discover how technology is transforming healthcare by analyzing data, designing solutions, and building skills in problem-solving, innovation, and understanding how digital tools shape healthcare systems.",
    "subtitle": "Heal, Innovate, Lead: HealthTech Virtual Internship Program",
    "lead": "Turn Curiosity into Real-World Health Skills in 6 Weeks | For Ages 13–18",
    "promise": "From Zero Experience to Understanding Real HealthTech",
    "perks": [
      "No medical or technical background required",
      "Live sessions every Saturday",
      "Learn alongside real healthtech professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=256",
    "sourceUrl": "https://alearninglab.com/virtual-internships/virtual-healthtech-shadowing-program/",
    "mentors": [
      {
        "name": "Summaiya Aftab",
        "bio": "Summaiya is a Dubai-based science educator with a Master’s degree in Life Sciences. Certified in biomedical research, biostatistical data analysis, systematic reviews, and research methodology, she brings a strong foundation in the scientific and analytical principles that underpin modern healthcare and health technology. Her expertise in evidence-based learning, student assessment, and research-driven thinking equips aspiring healthcare professionals with the critical scientific grounding needed to navigate and contribute to the evolving world of health and medicine.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Frame-1707483390.png"
      }
    ],
    "skillsTitle": "6 Weeks. Real Exposure. Real Growth.",
    "skillsIntro": "Use Google’s Teachable Machine to create and test your own smart health-tech model.",
    "skills": [
      {
        "title": "Explore HealthTech",
        "text": "Discover how technology is transforming healthcare - from wearables to AI diagnostics.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483394.png"
      },
      {
        "title": "Analyze Real Health Data",
        "text": "Work with actual health metrics and learn how professionals collect, read, and use data.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483398.png"
      },
      {
        "title": "Design a Health App",
        "text": "Sketch a real app interface that solves a health problem and think like a product designer.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483399.png"
      },
      {
        "title": "Think Ethically",
        "text": "Tackle real debates around AI, privacy, and consent in healthcare. Your opinion matters.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483397.png"
      },
      {
        "title": "See How Companies Use HealthTech",
        "text": "Real case studies from industry partners. Learn directly from working professionals.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483400.png"
      },
      {
        "title": "Earn A Certificate",
        "text": "Official certificate by A Learning Lab that will strengthen your academic and career profile.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483396.png"
      }
    ],
    "journeyTitle": "How It Works: Your 6-Week Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "What Is HealthTech?",
        "text": "Explore how technology meets healthcare. Understand wearables, health apps, telemedicine, and medical devices – and map the full healthtech landscape."
      },
      {
        "label": "Week 2",
        "title": "Health Data & Digital Health",
        "text": "Learn how health data is collected through apps and sensors. Build simple graphs and create your own one-day health data report."
      },
      {
        "label": "Week 3",
        "title": "HealthTech Solutions & User Experience",
        "text": "Analyze real health apps and learn to design from a patient’s perspective. Sketch a basic interface that solves a real health problem."
      },
      {
        "label": "Week 4",
        "title": "Wearables & Preventive Health (Case Study Week)",
        "text": "Compare wearable devices, interpret sample data, and see how real companies are using this technology to transform preventive care."
      },
      {
        "label": "Week 5",
        "title": "Ethics, Privacy & AI in HealthTech",
        "text": "Debate real-world scenarios on data privacy and AI in medicine. Write your own HealthTech Ethics Charter."
      },
      {
        "label": "Week 6",
        "title": "HealthTech Careers & Final Project",
        "text": "Discover career paths in healthtech – from engineering to entrepreneurship – and present your own healthtech idea to the group."
      }
    ],
    "experience": {
      "title": "Learn From Real HealthTech Professionals, Not Just Slides",
      "text": "Your child won’t just learn concepts – they’ll see exactly how engineers, designers, and data professionals solve real healthcare problems using technology.",
      "points": [
        "Live expert sessions",
        "Real case studies",
        "Guest speaker Q&A",
        "Behind-the-scenes industry insights",
        "Career pathway guidance",
        "Real-world health context"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "A Smart Step Towards Your Child’s Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from InteriorCompany",
        "Access to recordings​",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Ashmik Mehngi",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really liked the Virtual Engineering Shadowing program and thought it was a great way to learn. Our mentor was very knowledgeable and always made the sessions interesting, which made it easy to understand even the most difficult topics. She gave us useful information about different areas of engineering and encouraged us to think critically and ask questions. I especially enjoyed the practical parts of the course, like coding and CAD design. Her friendly and encouraging teaching style made a big difference, and the course gave me both knowledge and confidence to explore engineering further."
      },
      {
        "name": "Mohamed Siddig Mohyeldin Siddig",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really enjoyed the course in the engineer shadowing program. Our mentor had a very thorough teaching style that helped me clarify my understanding of the different engineering fields. The fun activities and tools made it easier to understand and apply what we learned."
      },
      {
        "name": "Leyl Bilgrami",
        "tag": "Virtual AI, Programming & Computer Science Shadowing",
        "text": "Our mentor was extremely clear and shared a lot of valuable information about the course. He was kind, engaging, and made me more interested in AI."
      },
      {
        "name": "Humayl Fazal",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The 6-week virtual job shadowing program gave me exposure to various engineering fields—from car design and robotics to sustainability and aerodynamics—while helping me gain industry insights and clarity on future career pathways."
      },
      {
        "name": "Syed Mahad Ahmed",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The engineering shadowing internship was really helpful. My mentor explained concepts clearly and gave me advice that made me more confident about engineering. Learning how engineering works in real life has made me feel more confident about my future."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "3:00 PM - 5:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "3:00 PM - 5:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)|",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)|",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any medical or technical background?",
        "a": "Not at all! This program is designed from the ground up for beginners. We start with the basics of what healthtech is and build up week by week. If your child is curious about health or technology – or both – they’re ready to join."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with a stable internet connection and a working webcam. All tools used in the program are free, including spreadsheet tools for data activities and digital design platforms, as needed. Setup instructions will be sent before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We strongly encourage students to attend every live session, as real-time interaction, group discussions, and case study activities are central to the experience. That said, if an unavoidable situation arises, all sessions are recorded in high quality and made available within 24 hours through the student dashboard so your child can catch up at their own pace."
      },
      {
        "q": "Which company partners with the program?",
        "a": "We collaborate with innovative healthtech organizations across various areas of the industry. The partner company is updated each cohort but always features teams doing meaningful, real-world work at the intersection of health and technology. The current partner for this internship will be confirmed and communicated to enrolled students ahead of the program."
      },
      {
        "q": "Is this live instruction or pre-recorded content?",
        "a": "100% live, every Saturday. Students interact directly with the instructor, ask questions in real time, and collaborate with peers in breakout rooms. The live format is fundamental to how this program delivers genuine learning and engagement."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are warmly welcome to observe during the first session. After that, we find students engage more openly and confidently without parents watching – but you’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this help with college or future applications?",
        "a": "Yes. Students complete a hands-on portfolio project, earn a certificate, and develop a working understanding of a high-growth industry. These are concrete talking points for college essays, interviews, and scholarship applications – and genuine skills that set your child apart."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate of participation upon successful completion. Many go on to explore further learning in health, data, or design. Post-internship, students also have the opportunity to enroll in any of A Learning Lab’s specialized programs and can book a complimentary 30-minute consultation to plan their next academic or career steps."
      },
      {
        "q": "What is your refund policy?",
        "a": "We do not offer refunds. However, if a student is unable to attend due to an unforeseen circumstance and informs our team in advance, we will allow them to transfer their enrollment to any future program offered by A Learning Lab using the fee already paid."
      }
    ]
  },
  {
    "slug": "dentistry-virtual-internship",
    "industry": "health",
    "title": "Dentistry Virtual Internship",
    "metaTitle": "Dentistry Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Step into the world of dentistry through real, stage-by-stage patient cases – developing skills in diagnosis, patient… Live online, ages 13-18.",
    "summary": "Step into the world of dentistry through real, stage-by-stage patient cases – developing skills in diagnosis, patient communication, and treatment planning that compound week over week.",
    "subtitle": "From Diagnosis to Patient Care: Experience Dentistry in Action",
    "lead": "3-Week Guided Virtual Shadowing for Ages 13–18",
    "promise": "Your First Step Towards a Career in Dentistry",
    "perks": [
      "No prior dental knowledge required",
      "Live-immersive sessions every week",
      "Every session built on real case-based patient scenarios"
    ],
    "duration": "3 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=308",
    "sourceUrl": "https://alearninglab.com/dentistry-virtual-shadowing-program/",
    "mentors": [
      {
        "name": "Dr. Lorna Sharma",
        "bio": "Dr. Lorna is a UK-born, NHS-trained Family Medicine Consultant with 10+ years of experience across emergency medicine, internal medicine, paediatrics, obstetrics & gynaecology, psychiatry, and general surgery. Now based in Dubai and licensed by the Dubai Health Authority, she brings extensive UK and UAE clinical experience, offering students valuable insight into global healthcare systems, medical specialisms, and real-world clinical practice. Her expertise also spans women’s health, longevity medicine, hormone care, mother-and-baby care, preventative healthcare, and holistic patient-centered treatment.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/05/Frame-1-1024x1024.png"
      },
      {
        "name": "Dr. Miaad",
        "bio": "Dr. Miaad, an MBBS graduate from China Medical University, is a student counselor with experience supporting students studying abroad. With a focus on clear communication, cultural insight, and trust-building, she helps students confidently navigate academic choices and adapt to new environments.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483347.png"
      },
      {
        "name": "Dr. Anjali Gupta",
        "bio": "Dr. Anjali Gupta is a Consultant in Obstetrics, Gynaecology, and Laparoscopic Surgery with extensive experience in women’s health and maternal care. An MBBS Gold Medalist with MRCOG (UK) and other advanced qualifications, she brings strong clinical insight and ethical practice. As a mentor, she guides interns in developing sound clinical judgment and patient-focused care.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Dr.-Anjali-Gupta-Medicine-VI-1.png"
      },
      {
        "name": "Rayan",
        "bio": "Rayan is a medical student and experienced admissions counselor who understands the challenges of medical school firsthand. Drawing from her own journey, she provides practical, empathetic guidance to help aspiring doctors navigate admissions, make informed decisions, and find the path that truly suits them.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483346.png"
      },
      {
        "name": "Varshitaa Prasad",
        "bio": "Varshitaa is a second-year medical student at The University of Manchester. Having successfully navigated the medical school application process and secured offers from all four UK universities she applied to, she brings recent, first-hand experience to her role as a mentor. She is passionate about guiding students through the complexities of medical admissions and is committed to offering practical, honest support throughout their journey.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Varshitaa.png"
      },
      {
        "name": "Dr. Abhimanyu Baruah",
        "bio": "Dr. Abhimanyu is currently practicing medicine in the UK. He graduated from the University of Nottingham after completing his earlier studies in Dubai. Having recently achieved an All-India Rank of 330, Abhi will begin his Internal Medicine Training in London next year. His academic journey and competitive success have equipped him with valuable insights into the demanding path toward medical school and postgraduate training.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/06/Abhimanyu-Baruah.png"
      },
      {
        "name": "Arya Ravi",
        "bio": "Arya Ravi is a third-year MBBS student at RRRMCH, Bangalore, with a focused interest in Cardiothoracic and Vascular Surgery. His clinical exposure spans two years of active postings across departments – observing OT and OPD cases – complemented by a one-month doctor shadowing and observership at NMC Royal Khalifa City, Abu Dhabi, in the Interventional Cardiology department. A consistent academic performer with First Class results across all three years, he has delivered seminars and lectures on clinical and pre-clinical subjects, and participated in hands-on workshops covering CPR, Basic First Aid, suturing techniques, road traffic accident management, and mental health wellness. Arya brings the perspective of a driven, community-conscious medical student with real clinical exposure and a genuine passion for the future of surgery and patient care.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Frame-1707483417.png"
      }
    ],
    "skillsTitle": "3 Weeks. 6 Sessions. Real Skills.",
    "skills": [
      {
        "title": "Hands-On Clinical Practice",
        "text": "Diagnose real-style patients - starting with only a chief complaint, just like a real dentist - then reveal exam findings, X-rays, and a final diagnosis.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-highly-realistic-clinic_rlr8RZuxtc-1.jpg"
      },
      {
        "title": "Dental Knowledge in Action",
        "text": "Learn tooth anatomy, read X-rays using the same dark/light rule dentists use, and apply a real diagnostic checklist to spot decay, bone loss, and infection.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-highly-realistic-dental_xgxIG9EjfW.webp"
      },
      {
        "title": "Problem Solving Under Pressure",
        "text": "Triage six patients arriving at once, and make the exact call a real dental team makes in the first minutes after a tooth is knocked out.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-realistic-dental-educat_brbe3G45Y2.webp"
      },
      {
        "title": "Patient-Ready Communication",
        "text": "Learn \"tell-show-do\" technique - method dentists use with nervous patients - paired roleplay with swapped roles.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-friendly-dentist-mentor_3G3rh4tREY.webp"
      },
      {
        "title": "Ethics & Professionalism",
        "text": "Weigh real dilemmas: how much imaging is enough, and what you do when the ideal treatment plan isn't affordable.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-professional-dentist-le_l7lTAUogv9-1.jpg"
      },
      {
        "title": "Earn a Certificate",
        "text": "Walk away with real-time learnings and a certificate of completion, helping in strengthening your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/magnific_a-realistic-dental-consul_DBD4LxQpcl.webp"
      }
    ],
    "journeyTitle": "How It Works: Your Journey Across 6 Sessions",
    "weeks": [
      {
        "label": "Session 1",
        "title": "Welcome to Dentistry: The Mouth-Body Connection",
        "text": "Learn tooth anatomy from crown to pulp, discover how oral health connects to heart disease and diabetes, then diagnose your first patient case whose jaw pain and swelling lead you through exam findings, an X-ray, and a real diagnosis – pericoronitis."
      },
      {
        "label": "Session 2",
        "title": "Reading the Clues: Imaging, Tools & Diagnosis",
        "text": "Learn the tools and X-ray types dentists rely on daily, then work a real case where a routine checkup nearly gets missed – until imaging reveals what a clean exam alone couldn’t catch. Finish with “X-Ray Detective,” spotting hidden findings across three new images."
      },
      {
        "label": "Session 3",
        "title": "Chairside Skills: Patient Communication & Prevention",
        "text": "Learn the plaque-and-acid cycle and why prevention beats treatment, then meet Alex, 13 – anxious, avoidant, and afraid of the dentist. Practice the “tell-show-do” framework through three roleplay scenarios, swapping dentist and patient roles."
      },
      {
        "label": "Session 4",
        "title": "Dental Emergencies & Trauma Care",
        "text": "Learn the exact first-aid steps for a knocked-out tooth, then work through a case in real time – a sports injury where imperfect first response changes the odds. Close with “ER Triage,” ranking six arriving patients by true urgency."
      },
      {
        "label": "Session 5",
        "title": "Building a Treatment Plan",
        "text": "Bust common myths (root canals aren’t as painful as you think), learn the real priority framework dentists use – urgent first, prevention next, cosmetic last – then build and defend a full treatment plan for Priya, 17, who has three issues at once."
      },
      {
        "label": "Session 6",
        "title": "Meet the Specialists: Career Pathways in Dentistry",
        "text": "Meet six dental specialties and see how dentists actually train, then rotate through four new patient cases, referring each one to the right specialist using everything you’ve learned. Close with a personal career-pathway worksheet and your certificate of completion."
      }
    ],
    "experience": {
      "title": "Experience Dentistry the Way a Working Dentist Does",
      "text": "This isn’t a lecture series – it’s a virtual internship built entirely around real-style patient cases, revealed the same way real information arrives in a dental office: symptom first, clues second, diagnosis last. Your child won’t just learn about dentistry – they’ll practice thinking like a dentist.",
      "points": [
        "Diagnose patients stage by stage, from chief complaint to diagnosis",
        "Read real X-rays using a dentist's own dark/light rule",
        "Roleplay real patient conversations using the \"tell-show-do\" method",
        "Make a real triage call under time pressure",
        "Build and defend a full, prioritized treatment plan",
        "Match real patient cases to the right dental specialist"
      ]
    },
    "testimonials": [
      {
        "name": "Rwaa Ramadan Elfayoumy",
        "tag": "Virtual Shadowing with NHS Doctors",
        "text": "The explanations done by the mentors were excellent; it made me more aware of how I feel about pursuing a career in medicine. The sessions with the doctor were engaging as she gave as an insight into what her job experience is like and how to deal with patients. I gained a lot of skills in terms of patient communication - how to deliver bad news, verbal and non verbal behaviour towards patient, and how to deal with uncooperative patients - as well as being able to understand the non verbal signs from a patient. In addition to that, I gained further insight into how communication and leadership skills are important in saving a patient’s life. For example, when met with a code blue case, there must be one person who is leading. The mentors explained that statement really well, and explained the other roles in an ACLS. I would like to say thank you to all the mentors, and wish them the best of luck in their career!"
      },
      {
        "name": "Siddhant Pandita",
        "tag": "Virtual Doctor Shadowing",
        "text": "I found the course to be very well composed, with the key ideas of what it means to be a doctor in the NHS clearly and effectively presented. Therefore, I have no specific topic or component that I would like A Learning Lab to add to the curriculum."
      },
      {
        "name": "Laibah",
        "tag": "Virtual Doctor Shadowing",
        "text": "I believe the curriculum is already very comprehensive, as it includes case studies, hypothetical scenarios, and potential patient interactions. There isn’t anything more I would suggest adding since it effectively covers all key aspects of medical shadowing."
      },
      {
        "name": "Angela Eva Biju",
        "tag": "Virtual Shadowing with NHS Doctors",
        "text": "This has been my very first shadowing experience, and it has definitely given me insights into what the field of medicine really looks like. My mentors for the sessions, Miss Moniba and Miss Imaan have been the best at clarifying our queries, whether big or small- and through their demeanor they've created an open atmosphere of discussion and learning. The knowledge they've imparted has definitely impacted me, as I'm always left with something or the other to research and no more about at the end of each session! This program has, no doubt, strengthened the passion I have for medicine and healthcare. Thank you very much!"
      },
      {
        "name": "Reisha Gandhi",
        "tag": "Virtual Internship Medicine",
        "text": "In the Virtual Medical Course, I was able to learn about different specialties and what each of them require. It was a very interesting course and I was able to speak to different Doctors in each field as well."
      },
      {
        "name": "Leen Osman",
        "tag": "Virtual Internship Medicine",
        "text": "I learnt a lot of new information during these sessions and found them to be really enlightening. One thing that was really interesting was getting to learn about all the many different types of specialties as well as some basic skills that are needed in these specialties."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "2:00 PM - 4:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 - 22 October, 2026 (Sundays till Thursday)",
        "time": "1:00 PM - 3:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 - 29 October, 2026 (Sundays till Thursday)",
        "time": "1:00 PM - 3:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "2:00 PM - 4:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "2:00 PM - 4:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "2:00 PM - 4:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior medical knowledge or clinical experience?",
        "a": "Absolutely not. The program is designed for complete beginners. Week 1 starts with tooth anatomy and basic diagnostic thinking, and every skill after that builds directly on what came before. If your child is curious about dentistry, that’s the only prerequisite."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are calibrated specifically for this age group."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "A computer (Mac or Windows) with reliable internet and a webcam. All tools are free and browser-based, including the shared digital whiteboard used for X-ray labeling and case work. Setup instructions go out one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live. Students work through real patient cases with a facilitator in real time – reading X-rays, debating a diagnosis, running roleplay, and defending their reasoning out loud. This is active clinical thinking, not passive watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both. Students reason through cases individually before group discussion, and pair up directly for roleplay – even with just two students, one plays dentist and one plays patient, then they swap."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students work through six real, case-based diagnostic challenges – including two based on real published dental case reports – and finish with a personal career-pathway mapping worksheet grounded in specific moments from the program. The certificate of completion reflects genuine clinical reasoning, not passive attendance."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate of completion and keep their career-pathway worksheet. Many go on to explore school science clubs, health societies, or advanced biology coursework. Post-program, students can also book a complimentary 30-minute consultation with A Learning Lab’s experts to plan next steps toward dentistry."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "ui-ux-product-design-virtual-internship",
    "industry": "ui-ux-creative-designing",
    "title": "UI/UX Product Design Virtual Internship",
    "metaTitle": "UI/UX Product Design Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Design digital experiences by learning tools like Figma, conducting user research, and building prototypes while developing… Live online, ages 13-18.",
    "summary": "Design digital experiences by learning tools like Figma, conducting user research, and building prototypes while developing skills in visual design, usability, problem-solving, and creating intuitive, user-centered products.",
    "subtitle": "Discover How Design Works: Explore UI/UX and Digital Product Creation",
    "lead": "Experiential 6-Week Virtual Internship for Ages 13–18",
    "promise": "From Zero Experience to Young Designer",
    "perks": [
      "No design experience needed",
      "Live sessions every Saturday",
      "Shadow UI/UX designers and product teams"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=253",
    "sourceUrl": "https://alearninglab.com/ui-ux-product-design-shadowing-program/",
    "partnership": {
      "logo": "https://alearninglab.com/wp-content/uploads/2026/01/odoo_logo.png",
      "text": "Odoo brings real-world enterprise design into your learning experience. Our industry partner provides hands-on exposure to how professional interfaces are created for complex business software used by millions globally. Through live case studies, practical examples, and behind-the-scenes insights, students explore how designers create intuitive experiences, build design systems, and solve user problems using tools and methods that are also used by professionals in the industry."
    },
    "mentors": [
      {
        "name": "Sumeet Ambegaokar",
        "bio": "A seasoned marketing and business leader with over 30 years of experience across education, healthcare, retail, telecom, and FMCG. An MBA in Marketing and a Mechanical Engineer by education, he combines strategic thinking with operational excellence. Sumeet has led large-scale brand transformations, digital marketing, admissions growth, corporate partnerships, and customer experience initiatives across regional and international organizations. Recognized globally for leadership and brand-building excellence, his strengths lie in strategic planning, team leadership, performance-driven marketing, and building high-impact partnerships.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/Frame-1707483384.jpg"
      },
      {
        "name": "Avani Jain",
        "bio": "Avani works as the Head of Visual Communications, holding a Bachelor’s degree in Animation and a strong foundation in visual storytelling. With extensive experience across creative design, video production, and brand communication, she leads end-to-end creative processes and develops visuals that connect with students and families. Her strengths include structured creative thinking, attention to detail, and translating ideas into clear, impactful content. She enjoys balancing creativity with consistency to ensure every output aligns with the brand’s values and voice. Leading creative initiatives and seeing their real-world impact continues to be both fulfilling and motivating for her.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/Frame-1707483383.jpg"
      },
      {
        "name": "Mansi Dutta",
        "bio": "Mansi is a UI/UX Designer at Appinventiv with over 4.5 years of professional experience in designing intuitive, user-centric digital products for web and mobile platforms. She specializes in user research, wireframing, prototyping, interaction design, and design systems, with expertise in creating seamless and visually engaging user experiences. Holding an Engineering degree in Electronics and Telecommunication Engineering (ETE), she combines technical understanding with creative problem-solving to build impactful products. As a mentor, Mansi is passionate about guiding aspiring designers through practical industry insights, product thinking, and real-world UI/UX design experience.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-25-at-12.41.26.jpeg"
      }
    ],
    "skillsTitle": "6 WEEKS. REAL SKILLS. REAL PROGRESS.",
    "skills": [
      {
        "title": "Hands-On Design Practice",
        "text": "Create wireframes, visuals, and interactive prototypes to experience UI/UX design and product development processes.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483300.png"
      },
      {
        "title": "Design in Action",
        "text": "Apply concepts from user research, interface design, prototyping, and usability testing to create digital experiences.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483307.png"
      },
      {
        "title": "Problem-Solving Skills",
        "text": "Analyze user needs and design challenges to develop reasoning, visual thinking, and decision-making skills.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483302.png"
      },
      {
        "title": "Practical Skills Development",
        "text": "Learn Figma, user research, wireframing, visual design, typography, color theory, and interactive prototyping techniques.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      },
      {
        "title": "Shadow UI/UX Designers",
        "text": "Explore careers in design, shadow product professionals, and gain insights into design workflows and career pathways.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483308.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Present your complete website design project and receive an official certificate to strengthen your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      }
    ],
    "journeyTitle": "HOW IT WORKS: YOUR 6-WEEK TRANSFORMATION JOURNEY",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to UI/UX Design and Design Thinking",
        "text": "Discover the difference between UI (how it looks) and UX (how it works), master design principles like visual hierarchy and contrast, learn Figma, and analyze how companies make complex business software intuitive for millions of users."
      },
      {
        "label": "Week 2",
        "title": "User Research, Personas, and User Flows",
        "text": "Conduct real user interviews using professional techniques, create detailed personas based on actual research, and map user journeys identifying pain points – just like professional designers do for diverse global users."
      },
      {
        "label": "Week 3",
        "title": "Wireframing and Layout Design",
        "text": "Build professional wireframes using grid systems, master visual hierarchy and responsive design for desktop, tablet, and mobile, and study the partner company’s information architecture – how they organize 40+ apps so users find what they need instantly."
      },
      {
        "label": "Week 4",
        "title": "Visual Design – Color, Typography, and Imagery",
        "text": "Create cohesive color palettes using color theory, master font pairing and type scales, build your complete style guide, and analyze the partner company’s clean, modern visual design that makes enterprise software feel approachable."
      },
      {
        "label": "Week 5",
        "title": "Prototyping, Interactions, and Micro-Animations",
        "text": "Build interactive prototypes with clickable flows and smooth transitions, add micro-animations that delight users, and test your designs with real users while iterating based on feedback."
      },
      {
        "label": "Week 6",
        "title": "Present Your Design and Explore Careers in UI/UX",
        "text": "Present your complete website design project professionally, build your portfolio with a full case study, and explore UI/UX career pathways including design internships and professional opportunities."
      }
    ],
    "experience": {
      "title": "Discover UI/UX Design in Action",
      "text": "This is more than an online course – step into the shoes of a UI/UX designer and explore real-world applications of digital product design. Students will engage with real-world scenarios, interactive case studies, and gain practical insights into how designers work across research, wireframing, visual design, and prototyping.",
      "points": [
        "Live case studies",
        "Shadow practicing entrepreneurs",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Hands-on practice"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "A Smart Step Towards Your Child’s Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from InteriorCompany",
        "Access to recordings​",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Ashmik Mehngi",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really liked the Virtual Engineering Shadowing program and thought it was a great way to learn. Our mentor was very knowledgeable and always made the sessions interesting, which made it easy to understand even the most difficult topics. She gave us useful information about different areas of engineering and encouraged us to think critically and ask questions. I especially enjoyed the practical parts of the course, like coding and CAD design. Her friendly and encouraging teaching style made a big difference, and the course gave me both knowledge and confidence to explore engineering further."
      },
      {
        "name": "Mohamed Siddig Mohyeldin Siddig",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really enjoyed the course in the engineer shadowing program. Our mentor had a very thorough teaching style that helped me clarify my understanding of the different engineering fields. The fun activities and tools made it easier to understand and apply what we learned."
      },
      {
        "name": "Leyl Bilgrami",
        "tag": "Virtual AI, Programming & Computer Science Shadowing",
        "text": "Our mentor was extremely clear and shared a lot of valuable information about the course. He was kind, engaging, and made me more interested in AI."
      },
      {
        "name": "Humayl Fazal",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The 6-week virtual job shadowing program gave me exposure to various engineering fields—from car design and robotics to sustainability and aerodynamics—while helping me gain industry insights and clarity on future career pathways."
      },
      {
        "name": "Syed Mahad Ahmed",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The engineering shadowing internship was really helpful. My mentor explained concepts clearly and gave me advice that made me more confident about engineering. Learning how engineering works in real life has made me feel more confident about my future."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "2:00 PM - 4:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "2:00 PM - 4:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior knowledge or experience in design to participate?",
        "a": "Absolutely not! This program is designed specifically for beginners with no prior knowledge of UI/UX design. We start with the fundamentals – basic design principles, user research methods, and how design concepts apply to real-life digital products – and build step by step from there. If your child is curious about how apps and websites work and enjoys learning, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including Figma for design work and collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers and designers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual internship every Saturday. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on design activities, and ask questions in real time. The live, hands-on format is essential – this is active learning and shadowing in UI/UX design, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper marketing questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual learning with collaborative activities. Students work on their own website design project while also participating in team exercises and group discussions. This mirrors how real design teams collaborate in professional settings."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a UI/UX design project that demonstrates real creative, analytical, and problem-solving skills. The certificate issued reflects their initiative, hands-on learning, and exposure to practical design concepts."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate upon successful completion of the internship. Many use this strong foundation to join school design clubs, participate in design competitions, build personal portfolio projects, or take advanced design coursework. After the internship, students also have the opportunity to enroll in A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey in UI/UX design and related fields."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "architecture-and-design-virtual-internship",
    "industry": "ui-ux-creative-designing",
    "title": "Architecture & Design Virtual Internship",
    "metaTitle": "Architecture & Design Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Explore how spaces are designed by applying design thinking, sustainability principles, and spatial planning while building… Live online, ages 13-18.",
    "summary": "Explore how spaces are designed by applying design thinking, sustainability principles, and spatial planning while building skills in creativity, problem-solving, and understanding architecture, interiors, and urban design.",
    "subtitle": "Step Into the World of Architecture, Design & Sustainability",
    "lead": "Hands-on 6-Week Virtual Architecture Internship for Ages 13–18",
    "promise": "Six Weeks. Real Skills. Real Transformation.",
    "perks": [
      "No prior architecture or design background required",
      "Live sessions every Saturday",
      "Learn alongside architecture professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=245",
    "sourceUrl": "https://alearninglab.com/virtual-internships/architecture-design-sustainability-shadowing/",
    "mentors": [
      {
        "name": "Hashem Al Saydali",
        "bio": "Hashem is an architect, interior designer, and visualization artist having an MA in Interior and Spatial Design at University of Pécs. With experience across residential, commercial, workplace, adaptive reuse, and visualization projects, he has contributed to works such as the Student Gateway Centre at University of Pécs, Körber Hungária workplace renovation, Privacy Room for IFT Saudi Arabia, Unreal Engine Study – P2ciptura Apartment, and many more. He combines creativity, conceptual thinking, and human-centered design to help learners understand how spaces shape culture, interaction, and everyday life.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/05/Frame-2-1024x1024.png"
      }
    ],
    "skillsTitle": "What You Will Learn",
    "skills": [
      {
        "title": "Design Thinking",
        "text": "Understand how space, form, light, and function come together to shape human experience.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/mentor-pointing-at-a-desi_2741186714-scaled.webp"
      },
      {
        "title": "Interior Design",
        "text": "Explore color psychology, lighting, and spatial flow to design spaces that influence mood.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/closeup-collaborative-han_2741210700-scaled.webp"
      },
      {
        "title": "Urban Planning",
        "text": "Design cities that balance livability, movement, sustainability, and community needs.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/freepik_closeup-of-hands-position_2741282840.webp"
      },
      {
        "title": "Sustainable Design",
        "text": "Build eco-conscious architecture using green materials, renewable energy, and water conservation.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/freepik_closeup-of-hands-assembli_2741339049.webp"
      },
      {
        "title": "Restoration & Legacy",
        "text": "Preserve historical architecture while thoughtfully integrating modern functionality and aesthetics.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/freepik_students-hands-studying-r_2741371334.webp"
      },
      {
        "title": "Earn a Certificate",
        "text": "Receive a certificate from the partner company, recognising your architecture and design experience.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/freepik_bright-modern-design-stud_2741126954.webp"
      }
    ],
    "journeyTitle": "How It Works: Your 6-Week Transformation",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Design Thinking & User Experience",
        "text": "Explore how design impacts human behavior and well-being. Analyse well vs poorly designed spaces and tackle your first design challenge – reimagining a familiar space. Discover how the world’s best architects always begin with the human being at the center of every decision."
      },
      {
        "label": "Week 2",
        "title": "Interior Design & Spatial Experience",
        "text": "Dive into color psychology, lighting, and furniture layout. Design a themed café for a target audience and present your concept for peer feedback. Learn how the subtlest design choices can completely change how people feel inside a space."
      },
      {
        "label": "Week 3",
        "title": "Urban Design & City Planning",
        "text": "Learn how architects design cities. Evaluate real city layouts and build your own mini city balancing residential, commercial, and recreational zones. Tackle real urban challenges like traffic, lack of green space, and accessibility for people of all abilities."
      },
      {
        "label": "Week 4",
        "title": "Designing for the Future – Sustainability in Architecture",
        "text": "Explore green buildings, energy efficiency, and eco-friendly materials. Design a sustainable home incorporating renewable energy and water conservation strategies. Examine how architecture is one of the most powerful tools humanity has in responding to climate change. Leave with a project that shows you can think beyond aesthetics."
      },
      {
        "label": "Week 5",
        "title": "Restoration & Historical Architecture",
        "text": "Understand the importance of heritage and the challenges of restoration. Redesign a historical landmark while preserving its identity and debate: should historic buildings always be preserved?"
      },
      {
        "label": "Week 6",
        "title": "Reflection & Careers in Architecture",
        "text": "Present your project and explore career pathways – architect, interior designer, urban planner, landscape architect, and more. Leave with a certificate, a portfolio piece, and a clear sense of where your passion for design could take you next."
      }
    ],
    "experience": {
      "title": "Learn From A Real Company, Not Just Textbooks",
      "text": "This is more than an online course – it’s a virtual shadowing experience into the world of architecture and design. Students engage with real-world case studies, industry examples, and hands-on challenges that reflect how professional designers actually work.",
      "points": [
        "Live Case",
        "Portfolio-Worthy",
        "Shadow Architecture",
        "Behind-the-Scenes",
        "Guest Speaker",
        "Peer Collaboration"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "A Smart Step Towards Your Child’s Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from InteriorCompany",
        "Access to recordings​",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Ashmik Mehngi",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really liked the Virtual Engineering Shadowing program and thought it was a great way to learn. Our mentor was very knowledgeable and always made the sessions interesting, which made it easy to understand even the most difficult topics. She gave us useful information about different areas of engineering and encouraged us to think critically and ask questions. I especially enjoyed the practical parts of the course, like coding and CAD design. Her friendly and encouraging teaching style made a big difference, and the course gave me both knowledge and confidence to explore engineering further."
      },
      {
        "name": "Mohamed Siddig Mohyeldin Siddig",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really enjoyed the course in the engineer shadowing program. Our mentor had a very thorough teaching style that helped me clarify my understanding of the different engineering fields. The fun activities and tools made it easier to understand and apply what we learned."
      },
      {
        "name": "Leyl Bilgrami",
        "tag": "Virtual AI, Programming & Computer Science Shadowing",
        "text": "Our mentor was extremely clear and shared a lot of valuable information about the course. He was kind, engaging, and made me more interested in AI."
      },
      {
        "name": "Humayl Fazal",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The 6-week virtual job shadowing program gave me exposure to various engineering fields—from car design and robotics to sustainability and aerodynamics—while helping me gain industry insights and clarity on future career pathways."
      },
      {
        "name": "Syed Mahad Ahmed",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The engineering shadowing internship was really helpful. My mentor explained concepts clearly and gave me advice that made me more confident about engineering. Learning how engineering works in real life has made me feel more confident about my future."
      },
      {
        "name": "Anvi Yadav",
        "tag": "Accelerated Virtual Internship - Engineering",
        "text": "I thought it was a great session, very kind mentor. He was very supportive and encouraging. I really liked the tasks that he gave so we had things to do, I would really like it if other lessons had that too. Thank you!"
      }
    ],
    "batches": [
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "4:00 PM - 6:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "4:00 PM - 6:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "5:00 PM - 7:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "5:00 PM - 7:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior architecture or design knowledge?",
        "a": "Not at all. The program is designed for complete beginners. We start with the fundamentals of how spaces are designed and how design impacts human experience, then build progressively. If your child is curious about design and comfortable using a computer, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimised for ages 13–18 (typically high school students). The content, pacing, and career discussions are tailored for this developmental stage."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam. All tools used are free and browser-based. Detailed setup instructions will be sent one week before the program begins."
      },
      {
        "q": "Is this live instruction or pre-recorded content?",
        "a": "100% live virtual sessions every Saturday. Students interact directly with instructors, collaborate in breakout rooms, and ask questions in real time."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We encourage full attendance as live sessions include team activities, discussions, and guest speakers. In the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both. Students work on their own design projects while also participating in team activities, peer feedback sessions, and group discussions – mirroring how real architecture teams collaborate."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1. After that, students engage more authentically when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "Cohorts are kept small (maximum 20 students) to allow differentiated support. Advanced students receive additional challenges, while beginners get extra guidance. Instructors are experienced at ensuring every student progresses confidently."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Yes. Students complete a design-focused project that demonstrates real creative and analytical thinking. The certificate reflects initiative, industry exposure, and specialised learning in architecture and design."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive an industry-recognised certificate upon successful completion. Many go on to explore design clubs, competitions, or advanced coursework. Students also have the opportunity to enrol in A Learning Lab’s specialised programs and can avail a complimentary 30-minute consultation to plan their academic and career journey in architecture and related creative fields."
      },
      {
        "q": "What's your refund policy?",
        "a": "We do not offer refunds. However, if a student is unable to attend the internship due to an unforeseen circumstance, they may inform our team in advance. In such cases, we will allow them to enroll in any future program by A Learning Lab using their previously paid fee."
      }
    ]
  },
  {
    "slug": "gaming-and-vr-virtual-internship",
    "industry": "gaming-innovation",
    "title": "Gaming & VR Virtual Internship",
    "metaTitle": "Gaming & VR Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Create immersive game experiences by designing gameplay, exploring VR tools, and building interactive projects while developing… Live online, ages 13-18.",
    "summary": "Create immersive game experiences by designing gameplay, exploring VR tools, and building interactive projects while developing skills in storytelling, logic, user experience, and understanding how games are built professionally.",
    "subtitle": "Build Games. Explore VR. Shape the Future.",
    "lead": "Hands-On 6-Week Virtual Gaming Internship for Ages 13-18",
    "promise": "Turn Your Game Ideas into a Real Game",
    "perks": [
      "No gaming or coding background required",
      "Live sessions every Saturday",
      "Shadow real gaming professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=252",
    "sourceUrl": "https://alearninglab.com/gaming-and-vr-virtual-shadowing-program/",
    "partnership": {
      "logo": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/images-4-1.png",
      "text": "PropVR brings real-world immersive technology into your learning experience. Our industry partner provides hands-on exposure to how virtual and 3D environments are created and used across gaming, education, real estate, and training. Through live demonstrations, practical examples, and behind-the-scenes insights, students explore how creators design interactive worlds, build engaging experiences, and use VR tools that are also used by professionals in the industry."
    },
    "mentors": [
      {
        "name": "Dr. Aishwarya Thara Bhai Anish",
        "bio": "Dr. Aishwarya is an AI-in-education strategist, researcher, and educator working at the intersection of artificial intelligence, learning sciences, and institutional transformation. As Director of AI in Education at Bettroi, she leads AI-driven initiatives across learning analytics, EdTech strategy, and institutional adoption. She is also the creator of AI-Shu, an AI-powered learning and analytics platform focused on personalized education and data-informed decision-making. With experience across IB, IGCSE, A-Levels, AP, and higher education, her work translates complex AI concepts into practical and ethical frameworks for future-ready learning.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Aiswarya-0-AI-and-CS-VI-1.png"
      },
      {
        "name": "Faseel Anwar",
        "bio": "Faseel is a Product Manager and an MBA graduate from the University of Oxford’s Saïd Business School, working at the intersection of AI, education, and student decision-making. With over a decade of experience spanning software engineering, product development, and EdTech entrepreneurship, he has built and scaled products from research and validation through launch and growth. His journey includes founding and scaling an education institute, building hybrid learning platforms, and delivering software solutions for clients across four continents.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Faseel-AI-and-CS-VI-1024x1024.jpeg"
      },
      {
        "name": "Redil Katal",
        "bio": "Redil Katal is an IIT–NIT alumnus and Physics Gold Medalist with research experience in Quantum Computing and Machine Learning at IIT Delhi. He holds an AI specialization in Robotics and TinyML from IIT Ropar and brings over eight years of international teaching experience. Redil is known for simplifying complex concepts in Physics and Computer Science for IB, IGCSE, A-Level, and AP students.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483348.png"
      },
      {
        "name": "Farhan Khan",
        "bio": "Farhan is a growth-focused marketing professional with over four years of experience in social media management, brand communication, and digital content strategy. Currently serving as Ad Content Strategist at BLKBOX.ai (Austin, Texas), he analyses gameplay mechanics, player behaviour, and progression systems to craft high-performing ad creatives – bridging the gap between game development fundamentals and strategic content execution. His deep understanding of the Godot Engine, GDScript, and gaming trends, combined with hands-on experience designing content frameworks and optimising campaign performance, makes him a sharp and relevant guide for anyone stepping into the world of gaming and immersive media.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Farhan.png"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Hands-On Game Design",
        "text": "Create and test your mini-games using beginner-friendly tools and game engines, learning how characters, environments, and interactions come to life.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483332.png"
      },
      {
        "title": "Storytelling & Mechanics",
        "text": "Apply game design principles to craft compelling narratives, engaging challenges, and rewarding player experiences.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483334.png"
      },
      {
        "title": "Real-World Problem Solving",
        "text": "Analyze gameplay scenarios, test mechanics, and iterate your designs to solve challenges like a professional game designer.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483335.png"
      },
      {
        "title": "Build Your Own Game",
        "text": "Bring your ideas to life by designing a playable game or interactive experience, applying UX principles, logic, and creativity to make it fun and immersive.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483336.png"
      },
      {
        "title": "Career & Industry Insights",
        "text": "Explore diverse gaming industry roles, work with case studies, and understand the skills, tools, and creative thinking used by game development teams.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483316.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Gain a certificate from PropVR that strengthens your profile and highlights your early exposure to real-world gaming and VR concepts.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483338.png"
      }
    ],
    "journeyTitle": "How it Works: Your 6-Week Transformation Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Gaming & Player Experience",
        "text": "Discover the evolution of games, understand key game genres, and learn foundational principles of game design and player experience."
      },
      {
        "label": "Week 2",
        "title": "Game Mechanics & Storytelling",
        "text": "Explore game engines and basic game logic, learning how player actions trigger responses. Build simple playable elements using tools like Google AI Studios, Unity, or Scratch-style platforms."
      },
      {
        "label": "Week 3",
        "title": "Game Design & User Experience",
        "text": "Dive deeper into how games actually work by designing core mechanics and player interactions. Learn how to create engaging gameplay loops, test user experience, and refine your game through feedback and iteration like a real game designer."
      },
      {
        "label": "Week 4",
        "title": "Testing, Iteration and Game Development",
        "text": "Learn how professionals value companies using multiple methods: asset-based, comparable analysis, and discounted cash flow. Calculate and interpret key financial ratios like professionals do."
      },
      {
        "label": "Week 5",
        "title": "Fundraising & Investor Engagement",
        "text": "Understand how games are made, published, and monetized. Explore platforms, industry case studies, and how gaming skills translate to broader career opportunities."
      },
      {
        "label": "Week 6",
        "title": "Reflection & Careers in Gaming",
        "text": "Reflect on your learning and showcase your games. Discover pathways in game design, VR, esports, startups, and hear directly from industry professionals."
      }
    ],
    "experience": {
      "title": "Experience How Gaming Works in the Real World",
      "text": "This is more than an online course, it’s a virtual shadowing experience into the emerging gaming industry. Your child will engage with real-world applications, industry examples, and practical insights into how modern gaming technologies operate.",
      "points": [
        "Live case studies",
        "Shadow gaming experts",
        "Guest speaker Q&A",
        "Behind-the-scenes insights"
      ]
    },
    "testimonials": [
      {
        "name": "Humayl Fazal",
        "tag": "",
        "text": "The 6-week virtual shadowing program gave me exposure to various fields while helping me gain industry insights and clarity on future career pathways."
      },
      {
        "name": "Syed Mahad Ahmed",
        "tag": "",
        "text": "The shadowing internship was really helpful. My mentor explained concepts clearly and gave me advice that made me more confident. Learning how things work in real life has made me feel more confident about my future."
      },
      {
        "name": "Ashmik Mehngi",
        "tag": "",
        "text": "I really liked the Virtual Shadowing program and thought it was a great way to learn. Our mentor was very knowledgeable and always made the sessions interesting. I especially enjoyed the practical parts of the course, like coding, and the experience gave me both knowledge and confidence to explore engineering further."
      },
      {
        "name": "Mohamed Siddig",
        "tag": "",
        "text": "I really enjoyed the course in the shadowing program. Our mentor had a very thorough teaching style that helped clarify my understanding. The fun activities and tools made it easier to understand and apply what we learned."
      },
      {
        "name": "Leyl Bilgrami",
        "tag": "",
        "text": "Our mentor was extremely clear and shared a lot of valuable information throughout the course. He was kind, engaging, and made me more interested in the subject."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "12:00 PM - 2:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "12:00 PM - 2:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior gaming or technical knowledge?",
        "a": "Absolutely not! This program is designed specifically for beginners with no prior gaming or technical background. We start with the fundamentals – how games are designed, how gameplay works, and how ideas turn into playable experiences – and build step by step from there. If your child is curious about games and comfortable using a computer, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual shadowing every Saturday. Your child will interact directly with instructors, work in team breakout rooms, collaborate on game design and gameplay activities, and ask questions in real time. The live, hands-on format is essential – this is active game shadowing, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual skill-building with collaborative team activities. Students work on their own game concept or mini project while also participating in team exercises and group playtesting sessions. This mirrors how real game development teams collaborate in the industry."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a game-focused project that demonstrates real creative and analytical skills. The certificate issued by PropVR reflects initiative, industry exposure, and specialized learning in the gaming domain."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive an industry-recognized certificate upon successful completion of the internship. Many go on to use this strong foundation to explore school game design clubs, esports initiatives, creative tech competitions, or advanced game development coursework. Post-internship, students also have the opportunity to enroll in A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey in gaming and related creative industries."
      },
      {
        "q": "What is PropVR?",
        "a": "PropVR is a virtual reality platform that makes creating and exploring 3D worlds and interactive experiences easy and accessible."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "aviation-virtual-internship",
    "industry": "gaming-innovation",
    "title": "Aviation Virtual Internship",
    "metaTitle": "Aviation Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Explore the world of aviation through simulations, case studies, and real scenarios while building skills in navigation… Live online, ages 13-18.",
    "summary": "Explore the world of aviation through simulations, case studies, and real scenarios while building skills in navigation, operations, problem-solving, and understanding how pilots, engineers, and airports function together.",
    "subtitle": "Fly, Build, Lead: Aviation Virtual Internship Program",
    "lead": "Turn Curiosity into Real-World Aviation Skills in 6 Weeks | For Ages 13–18",
    "promise": "From Zero Experience to Understanding How Aviation Really Works",
    "perks": [
      "No aviation or technical background required",
      "Live sessions every Saturday",
      "Learn alongside real aviation professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=251",
    "sourceUrl": "https://alearninglab.com/virtual-internships/virtual-aviation-shadowing-program/",
    "mentors": [
      {
        "name": "Sarfaraaz Ganie",
        "bio": "Sarfaraaz Ganie is an aviation-focused legal academic with a Bachelor’s degree in Law and a Master’s in Aviation Law from the University of Stellenbosch, South Africa. He has worked on aviation-focused projects with globally recognised organisations including Etihad Airways and The Economist, particularly in areas related to Sustainable Aviation Fuel and industry awareness initiatives across the UK, US, and Middle East. His work focuses on aviation policy, sustainability, regulatory frameworks, and bridging industry knowledge with practical, real-world application.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Sarfaraaz-Law-VI.jpeg"
      }
    ],
    "skillsTitle": "6 Weeks. Real Exposure. Real Growth.",
    "skills": [
      {
        "title": "Explore Aviation Careers",
        "text": "Discover the full world of aviation from cockpits to control towers - no prior knowledge needed.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483407.png"
      },
      {
        "title": "Experience a Virtual Cockpit",
        "text": "Use flight simulator software to experience real takeoff, navigation, and landing scenarios firsthand.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483404.png"
      },
      {
        "title": "Manage an Airport",
        "text": "Take on the role of dispatcher, controller, and operations manager in live team simulations.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483405.png"
      },
      {
        "title": "Think Like an Engineer",
        "text": "Diagnose a faulty aircraft scenario and work through it the way real aviation professionals do.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483408.png"
      },
      {
        "title": "See How the Industry Operates",
        "text": "Case studies and expert insights from working aviation professionals across every career track.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483403.png"
      },
      {
        "title": "Earn A Certificate",
        "text": "Official certificate by A Learning Lab that will strengthen your academic and career profile.",
        "image": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/02/Frame-1707483406.png"
      }
    ],
    "journeyTitle": "How It Works: Your 6-Week Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Aviation & Career Pathways",
        "text": "Discover the full scope of the aviation industry – commercial, private, cargo, and military – and meet the wide range of roles that keep it running."
      },
      {
        "label": "Week 2",
        "title": "Flying Careers: Pilots, Flight Attendants & Instructors",
        "text": "Learn the fundamentals of flight and explore careers in the cockpit and cabin. Experience takeoff, navigation, and landing through a virtual cockpit simulation."
      },
      {
        "label": "Week 3",
        "title": "Technical Careers: Mechanics, Avionics & Engineers",
        "text": "Go behind the wings. Understand aircraft systems, engines, and avionics – then diagnose a real faulty aircraft scenario the way engineers do."
      },
      {
        "label": "Week 4",
        "title": "Operations & Management: ATC, Dispatch & Airport Security",
        "text": "Step into air traffic control and airport operations. Manage live flight schedules, coordinate departments, and make quick decisions under pressure."
      },
      {
        "label": "Week 5",
        "title": "Administrative & Management Careers in Aviation",
        "text": "Explore the business side of aviation – from airline scheduling and logistics to HR, marketing, and customer relations. Build your own mini airline plan."
      },
      {
        "label": "Week 6",
        "title": "Reflection & Career Pathways in Aviation",
        "text": "Map your interests to real aviation career paths, get peer feedback, and hear directly from a working pilot, engineer, or operations professional."
      }
    ],
    "experience": {
      "title": "Learn From Real Aviation Professionals, Not Just Slides",
      "text": "Your child won’t just learn concepts – they’ll see exactly how pilots, engineers, controllers, and managers handle real challenges in one of the world’s most dynamic industries.",
      "points": [
        "Live expert sessions",
        "Real cockpit simulations",
        "Guest speaker Q&A",
        "Behind-the-scenes industry insights",
        "Career pathway guidance",
        "Real-world aviation context"
      ]
    },
    "testimonials": [],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday till Saturday)",
        "time": "12:00 PM - 2:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "11:00 AM - 1:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "11:00 AM - 1:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "12:00 PM - 2:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "9 January to 13 February, 2026 (Saturdays)",
        "time": "12:00 PM - 2:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any aviation or technical background?",
        "a": "Not at all. This program is built specifically for beginners with no prior knowledge of aviation or engineering. We start with the basics – what aviation is and who works in it – and build up steadily from there. If your child is curious about planes, airports, or careers in aerospace, they’re in exactly the right place."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with a reliable internet connection and a working webcam. All tools used in the program are free, including the flight simulator software used in Week 2. Detailed setup instructions will be sent to enrolled students before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We strongly encourage full attendance, as live simulations, team roleplay activities, and guest speaker sessions are central to the experience and cannot be fully replicated on replay. However, if an unavoidable situation arises, all sessions are recorded in high quality and made available within 24 hours via the student dashboard so your child can review the content at their own pace."
      },
      {
        "q": "Which company or professional partners with the program?",
        "a": "We collaborate with aviation professionals and organizations across different areas of the industry – from flight operations to airport management. The featured expert or partner may vary by cohort but always brings authentic, career-relevant experience. Details of the current cohort’s guest speaker and industry partner will be shared with enrolled students ahead of the program."
      },
      {
        "q": "Is this live instruction or pre-recorded content?",
        "a": "100% live, every Saturday. Students interact directly with the instructor, ask questions in real time, and participate in group simulations and breakout activities with their peers. The live format is what makes the learning genuine, interactive, and memorable."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe during the first session. After that, we find students engage more openly and take on leadership roles more confidently without parents watching – but you’ll have full access to session recordings and regular progress updates throughout the six weeks."
      },
      {
        "q": "Will this help with college or future applications?",
        "a": "Yes. Students complete a personal Aviation Career Map, participate in industry simulations, and earn a certificate – all of which are concrete additions to a college application, scholarship essay, or career conversation. More importantly, they’ll leave with a clear sense of where their interests align with real career paths."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate of participation upon successful completion. Many go on to explore further learning in engineering, logistics, or aerospace. Post-internship, students can also enroll in any of A Learning Lab’s specialized programs and book a complimentary 30-minute consultation to thoughtfully plan their next academic or career steps."
      },
      {
        "q": "What is your refund policy?",
        "a": "We do not offer refunds. However, if a student is unable to attend due to an unforeseen circumstance and informs our team in advance, we will allow them to transfer their enrollment to any future program offered by A Learning Lab using the fee already paid."
      }
    ]
  },
  {
    "slug": "finance-and-venture-capital-virtual-internship",
    "industry": "finance",
    "title": "Finance & Venture Capital Virtual Internship",
    "metaTitle": "Finance & Venture Capital Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Learn how money drives businesses by analyzing financial statements, valuing companies, and exploring real deals while building… Live online, ages 13-18.",
    "summary": "Learn how money drives businesses by analyzing financial statements, valuing companies, and exploring real deals while building skills in financial reasoning, investment thinking, and strategic decision-making used by professionals.",
    "subtitle": "From Startups to Stocks: Experience Finance & Venture Capital",
    "lead": "Hands-On 6-Week Virtual Shadowing for Ages 13-18",
    "promise": "From Balance Sheets to Real Business Decisions",
    "perks": [
      "No finance background required",
      "Live sessions every Saturday",
      "Shadow real financial professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=250",
    "sourceUrl": "https://alearninglab.com/virtual-internships/finance-and-venture-capital-virtual-shadowing-program/",
    "partnership": {
      "logo": "https://alearninglab.com/wp-content/uploads/2025/12/GIA-Logo-2.png",
      "text": "Global Institutional Advisors brings real-world financial expertise to your learning experience. Our industry partner provides exclusive case studies, guest speakers, and behind-the-scenes insights into how finance professionals analyze markets, value companies, and make strategic decisions."
    },
    "mentors": [
      {
        "name": "Vijaya",
        "bio": "A seasoned Strategic Finance Business Analyst with more than 20 years of experience, Vijaya has driven financial performance and strategic planning within the retail banking divisions of leading global institutions including Emirates NBD, Barclays, and ABN AMRO. She brings deep expertise in Financial Planning & Analysis, having led end-to-end P&L budgeting and rolling forecasts to support sustainable growth across retail segments.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/03/IMG-20260306-WA0000.jpg"
      },
      {
        "name": "Jonathan Melchiades Rutayebesibwa",
        "bio": "Jonathan, known as Mr Jona, is a Business and Economics educator from Tanzania with a Postgraduate Diploma in Education. He focuses on helping students bridge theory and real-world application through a student-centric teaching approach. Jonathan is passionate about building clarity, confidence, and practical understanding in business and finance education.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Jonathan-Finance-VI-1.png"
      },
      {
        "name": "Shokhrukhbek",
        "bio": "Shokhrukhbek holds a degree in International Economics from Georgetown University in Qatar and an MBA from NYU Stern School of Business in Abu Dhabi, where he received an 85% merit scholarship, specializing in Finance and Technology. He has advised 60+ students on admissions strategy, helping secure placements at Columbia, UMich, Georgetown, Babson, and UCLA.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/03/Screen-Shot-2026-03-14-at-5.39.39-PM.png"
      },
      {
        "name": "Saurabh Chaudhary",
        "bio": "Saurabh Chaudhary is a qualified Chartered Accountant (CA) and CFA Level 2 candidate with a graduation in Accounting and Finance, bringing deep expertise across financial planning, reporting, audit, and taxation. His career includes experience at two of the Big Four – PwC and EY – where he gained strong exposure to complex financial and regulatory environments across FP&A, external audit, financial reporting, budgeting, forecasting, and tax advisory. A guest lecturer and ACCA curriculum instructor alongside his professional practice, Saurabh brings to the internship a practitioner’s command of the financial systems, frameworks, and analytical rigour that underpin both corporate finance and investment decision-making.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/08/Saurabh.jpg"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Read Financial Statements",
        "text": "Analyze income statements, balance sheets, and cash flow statements just like professional analysts do.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483291.png"
      },
      {
        "title": "Value Actual Companies",
        "text": "Learn valuation methods used by investment bankers & equity researchers to see what companies are worth.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483292.png"
      },
      {
        "title": "Present Financial Analysis",
        "text": "Build a portfolio-worthy case study analyzing a real company's financial health and investment potential.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483293.png"
      },
      {
        "title": "Think Like a Finance Pro",
        "text": "Master analytical thinking, financial modeling basics, and decision-making.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483294.png"
      },
      {
        "title": "Shadow Finance Professionals",
        "text": "Access to professionals from GIA. See how they analyze deals, risk & advise clients.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483295.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Official certificate from Global Institutional Advisors to strengthen your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483296.png"
      }
    ],
    "journeyTitle": "How It Works: Your 6-Week Financial Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Finance",
        "text": "Discover how money flows through businesses and global markets. Learn the three core financial statements through an interactive T-shirt business simulation. Build your first mini income statement."
      },
      {
        "label": "Week 2",
        "title": "Reading A Financial Snapshot",
        "text": "Master balance sheets – understand what companies own, owe, and their true financial position. Become a “Balance Sheet Detective” solving real-world financial puzzles with your team."
      },
      {
        "label": "Week 3",
        "title": "Understanding Cash Flow",
        "text": "Track real cash movement and liquidity. Learn why profitable companies can still run out of cash. Predict cash needs and analyze operating, investing, and financing activities."
      },
      {
        "label": "Week 4",
        "title": "Company Valuation & Financial Ratios",
        "text": "Learn how professionals value companies using multiple methods: asset-based, comparable analysis, and discounted cash flow. Calculate and interpret key financial ratios like professionals do"
      },
      {
        "label": "Week 5",
        "title": "Fundraising & Investor Engagement",
        "text": "Shadow the fundraising process from startup pitch to venture capital deal. Analyze real case studies including top companies’ fundraising journey. Understand what investors look for in opportunities."
      },
      {
        "label": "Week 6",
        "title": "Career Pathways & Final Presentation",
        "text": "Meet a guest speaker from Global Institutional Advisors. Present your financial analysis project. Map your strengths to finance careers and build your first finance-focused CV."
      }
    ],
    "experience": {
      "title": "Shadow Working Professionals, Not Just Learn Theory",
      "text": "This isn’t a typical online course – it’s a virtual shadowing experience. Throughout the program, you’ll get exclusive access to professionals from Global Institutional Advisors who work with real companies, real deals, and real financial data every day.",
      "points": [
        "Live case studies",
        "Shadow working analysts",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Real-world context"
      ]
    },
    "testimonials": [
      {
        "name": "Vardaan Chaudhry",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "For my first two sessions, I had Sirius, who was incredibly helpful and guided us through the beginning. After, I had Abraham, who was incredibly diligent and professional, and went above and beyond to answer my questions about the topics discussed and outside it. He tailored the internship so it felt very personal, and answered questions I had about career prospects and higher education, sharing his personal experiences as well - even offering me personal advice about Management degrees at universities like UCL, where he attended."
      },
      {
        "name": "Ishan Amrith",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The mentors were very professional and introduced us to new fields of interest. As an economics student, I feel that the application of theory in a practical sense is very important. This need for real world examples was met as part of the coursework, we saw the examples of many companies and how they integrate theory in their everyday practices. Overall, this internship was very insightful and helpful with regards to furthering both my professional and personal interest in the subject of Finance."
      },
      {
        "name": "Advika Abhilash",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "Participating in the virtual internship on Banking and Finance was a valuable experience that deepened my understanding of the financial world. The course was thoughtfully structured, offering a perfect blend of theoretical knowledge and practical insights into key areas. Our mentor had an approachable nature, clear explanations, and real-world examples helped bridge the gap between academic concepts and practical application. They encouraged critical thinking, patiently answered our questions, and provided constructive feedback."
      },
      {
        "name": "Neil Pareek",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The course was the most practical finance course I've taken. The mentor combined clear teaching with deep, real case studies (DCF, comps, investment memos). I finished with sharper models, a clearer framework for valuation."
      },
      {
        "name": "Aydin Asif Kallangodan",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The course was insightful, and my mentor was supportive and knowledgeable throughout. It definitely strengthened my interest in the field."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "1:00 PM - 3:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior finance or business knowledge?",
        "a": "Absolutely not! This program is designed specifically for beginners with zero finance background. We start with the fundamentals – what money is, how businesses track it – and build systematically from there. If your child is curious about how companies work and can use a computer, they’re ready to start."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including Google Sheets for financial modeling and collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual shadowing every Saturday. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on financial analysis activities, and ask questions in real-time. The live, interactive format is essential – this is shadowing, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper analysis questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual skill-building with collaborative team activities. Students complete their own company analysis project while also participating in team exercises like the “Balance Sheet Detective Game” and group case studies. This mirrors real finance work environments."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a comprehensive company analysis project demonstrating real analytical skills. The certificate from Global Institutional Advisors shows initiative and specialized knowledge."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive an industry-recognized certificate upon successful completion of the internship. Many students use this strong foundation to pursue school finance clubs, investment competitions, business case challenges, or advanced finance coursework. Post-internship, students also have the opportunity to enroll in any of A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey ahead."
      },
      {
        "q": "Who are Global Institutional Advisors?",
        "a": "Global Institutional Advisors is our industry partner providing real-world financial expertise to the program. They’re an institutional finance advisory firm with 15+ years of experience across investment banking, corporate finance, and fintech sectors. Their professionals deliver exclusive case studies, provide career insights, and serve as guest speakers throughout the program."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "fintech-virtual-internship",
    "industry": "finance",
    "title": "Fintech Virtual Internship",
    "metaTitle": "Fintech Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Understand how digital finance works by using real tools, analyzing transactions, and designing solutions while building skills… Live online, ages 13-18.",
    "summary": "Understand how digital finance works by using real tools, analyzing transactions, and designing solutions while building skills in budgeting, cybersecurity, problem-solving, and understanding how technology transforms modern financial systems.",
    "subtitle": "Empowering the Next Generation of FinTech Leaders",
    "lead": "6-Week Industry-Led Virtual Shadowing for Ages 13-18",
    "promise": "From Basic Theory to Creating Your First FinTech Solution",
    "perks": [
      "No finance background required",
      "Live sessions every Saturday",
      "Shadow real financial professionals"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 20,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=249",
    "sourceUrl": "https://alearninglab.com/virtual-internships/virtual-fintech-shadowing-program/",
    "partnership": {
      "logo": "https://alearninglab.com/wp-content/uploads/2026/01/imagess.png",
      "text": "Urban Money brings real-world digital lending expertise to your learning experience. Our industry partner provides exclusive case studies, practical examples, and insights into how FinTech platforms help individuals and small businesses access loans, compare options, and manage repayments securely. Students gain an inside look into how technology simplifies borrowing, ensures safe transactions, and supports smart financial decision-making."
    },
    "mentors": [
      {
        "name": "Jonathan Melchiades Rutayebesibwa",
        "bio": "Jonathan, known as Mr Jona, is a Business and Economics educator from Tanzania with a Postgraduate Diploma in Education. He focuses on helping students bridge theory and real-world application through a student-centric teaching approach. Jonathan is passionate about building clarity, confidence, and practical understanding in business and finance education.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Jonathan-Finance-VI-1.png"
      },
      {
        "name": "Shokhrukhbek",
        "bio": "Shokhrukhbek holds a degree in International Economics from Georgetown University in Qatar and an MBA from NYU Stern School of Business in Abu Dhabi, where he received an 85% merit scholarship, specializing in Finance and Technology. He has advised 60+ students on admissions strategy, helping secure placements at Columbia, UMich, Georgetown, Babson, and UCLA.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/03/Screen-Shot-2026-03-14-at-5.39.39-PM.png"
      },
      {
        "name": "Saurabh Chaudhary",
        "bio": "Saurabh Chaudhary is a qualified Chartered Accountant (CA) and CFA Level 2 candidate with a graduation in Accounting and Finance, bringing deep expertise across financial planning, reporting, audit, and taxation. His career includes experience at two of the Big Four – PwC and EY – where he gained strong exposure to complex financial and regulatory environments across FP&A, external audit, financial reporting, budgeting, forecasting, and tax advisory. A guest lecturer and ACCA curriculum instructor alongside his professional practice, Saurabh brings to the internship a strong foundation in the financial systems, compliance frameworks, and analytical thinking that sit at the core of how financial technology is built, regulated, and scaled in today’s rapidly evolving digital economy.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/08/Saurabh.jpg"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Hands-On Practice",
        "text": "Experience real digital payment tools, online banking features, and investment apps to manage money efficiently.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483321.png"
      },
      {
        "title": "Smart Budgeting & Saving",
        "text": "Apply FinTech tools to track spending, set goals, and create actionable savings and budgeting plans.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483316.png"
      },
      {
        "title": "Real-World Problem Solving",
        "text": "Analyze everyday financial challenges and use digital tools to design practical solutions, simulating FinTech workflows.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483318.png"
      },
      {
        "title": "Career & Industry Insights",
        "text": "Work with case studies, understand the skills, and tools used by FinTech experts.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483320.png"
      },
      {
        "title": "Build a Fintech App",
        "text": "Design a simple digital finance app that solves real-world financial problems.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483322-1.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Official certificate from Urban Money that will help in strengthening your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483319.png"
      }
    ],
    "journeyTitle": "How it Works: Your 6-Week Transformation Journey",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Fintech",
        "text": "Explore how money has evolved from cash to digital tools and understand what FinTech means in everyday life. Build foundational awareness of digital payments, banking apps, lending, and investing."
      },
      {
        "label": "Week 2",
        "title": "Digital Payments & Online Banking",
        "text": "Learn how money moves online through cards, mobile payments, QR codes, and banking apps. Practice using online banking features while understanding secure and efficient digital transactions."
      },
      {
        "label": "Week 3",
        "title": "FinTech in Saving & Investing",
        "text": "Use FinTech tools to build smart money habits like saving, budgeting, and beginner investing. Explore concepts such as interest, risk, and returns through real-world scenarios and digital tools."
      },
      {
        "label": "Week 4",
        "title": "FinTech, Data & Cybersecurity",
        "text": "Understand how data powers digital finance and why protecting it is critical. Learn to identify scams, avoid fraud, and practice safe online financial behaviour."
      },
      {
        "label": "Week 5",
        "title": "Building & Using FinTech Solutions",
        "text": "Designing a mini FinTech app focused on solving real-world problems. Explore user experience, security, and real industry platforms through hands-on case studies."
      },
      {
        "label": "Week 6",
        "title": "Reflection and Careers in Fintech",
        "text": "Reflect and discover career pathways across technology, finance, design, data, and cybersecurity. Connect interests and skills to future education and FinTech career opportunities."
      }
    ],
    "experience": {
      "title": "Experience How FinTech Works in the Real World",
      "text": "This is more than an online course, it’s a virtual shadowing experience into the FinTech industry. Your child will engage with real-world applications, industry examples, and practical insights into how modern financial technologies operate.",
      "points": [
        "Live case studies",
        "Shadow working analysts",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Design a fintech app"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Shadowing That Brings Fintech to Life",
      "items": [
        {
          "title": "Small Cohorts",
          "text": "Maximum 20 students per session ensures your child gets individualized attention, answers to their questions, and meaningful participation in every activity."
        },
        {
          "title": "Industry Partnership",
          "text": "Urban Money provides exclusive access to real fintech professionals, live case studies, and career insights you won't find in traditional courses."
        },
        {
          "title": "Interactive Learning",
          "text": "Team activities, financial simulations, breakout room collaboration, and hands-on analysis projects. Students learn by doing, not just watching presentations."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "Curious about fintech?",
      "items": [
        "12 hours of live instruction",
        "Case studies from partner company",
        "Lifetime access to recordings",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Vardaan Chaudhry",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "For my first two sessions, I had Sirius, who was incredibly helpful and guided us through the beginning. After, I had Abraham, who was incredibly diligent and professional, and went above and beyond to answer my questions about the topics discussed and outside it. He tailored the internship so it felt very personal, and answered questions I had about career prospects and higher education, sharing his personal experiences as well - even offering me personal advice about Management degrees at universities like UCL, where he attended."
      },
      {
        "name": "Ishan Amrith",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The mentors were very professional and introduced us to new fields of interest. As an economics student, I feel that the application of theory in a practical sense is very important. This need for real world examples was met as part of the coursework, we saw the examples of many companies and how they integrate theory in their everyday practices. Overall, this internship was very insightful and helpful with regards to furthering both my professional and personal interest in the subject of Finance."
      },
      {
        "name": "Advika Abhilash",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "Participating in the virtual internship on Banking and Finance was a valuable experience that deepened my understanding of the financial world. The course was thoughtfully structured, offering a perfect blend of theoretical knowledge and practical insights into key areas. Our mentor had an approachable nature, clear explanations, and real-world examples helped bridge the gap between academic concepts and practical application. They encouraged critical thinking, patiently answered our questions, and provided constructive feedback."
      },
      {
        "name": "Neil Pareek",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The course was the most practical finance course I've taken. The mentor combined clear teaching with deep, real case studies (DCF, comps, investment memos). I finished with sharper models, a clearer framework for valuation."
      },
      {
        "name": "Aydin Asif Kallangodan",
        "tag": "Virtual Banking and Finance Shadowing Program",
        "text": "The course was insightful, and my mentor was supportive and knowledgeable throughout. It definitely strengthened my interest in the field."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior finance or business knowledge?",
        "a": "Absolutely not! This program is designed specifically for beginners with zero finance background. We start with the fundamentals – what money is, how businesses track it – and build systematically from there. If your child is curious about how companies work and can use a computer, they’re ready to start."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings"
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including Google Sheets for financial modeling and collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual shadowing every Saturday from 1:00 PM – 3:00 PM. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on financial analysis activities, and ask questions in real-time. The live, interactive format is essential – this is shadowing, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper analysis questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual skill-building with collaborative team activities. Students complete their own company analysis project while also participating in team exercises like the “Balance Sheet Detective Game” and group case studies. This mirrors real finance work environments."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a comprehensive company analysis project demonstrating real analytical skills. The certificate from Urban Company shows initiative and specialized knowledge. Most importantly, students can speak confidently about valuation methods and financial analysis."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive an industry-recognized certificate upon successful completion of the internship. Many students use this strong foundation to pursue school finance clubs, investment competitions, business case challenges, or advanced finance coursework. Post-internship, students also have the opportunity to enroll in any of A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey ahead."
      },
      {
        "q": "What is Urban Money?",
        "a": "Urban Money is a financial technology (FinTech) platform that simplifies how people access loans and credit products online."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "entrepreneurship-and-innovation-virtual-internship",
    "industry": "business-marketing",
    "title": "Entrepreneurship & Innovation Virtual Internship",
    "metaTitle": "Entrepreneurship & Innovation Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Build your own venture idea by validating ideas, conducting market research, and creating prototypes while developing skills in… Live online, ages 13-18.",
    "summary": "Build your own venture idea by validating ideas, conducting market research, and creating prototypes while developing skills in problem-solving, business strategy, customer understanding, and launching real-world products.",
    "subtitle": "Discover How Business Works: Explore Entrepreneurship and Innovation",
    "lead": "Experiential 6-Week Virtual Internship for Ages 13–18",
    "promise": "From Zero Experience to Young Entrepreneur",
    "perks": [
      "No business background needed",
      "6 weeks of hands-on business building",
      "Launch your own venture with powerful case studies"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=248",
    "sourceUrl": "https://alearninglab.com/entrepreneurship-and-innovation-shadowing-program/",
    "partnership": {
      "logo": "https://alearninglab.com/wp-content/uploads/2026/01/WG_red-bg-logo-1024x1024.webp",
      "text": "WickedGüd brings real-world entrepreneurship into your learning experience. Our industry partner provides hands-on exposure to how a startup is built from the ground up – from identifying market gaps to disrupting billion-dollar industries. Through live case studies, practical examples, and behind-the-scenes insights, students explore how founders validate ideas, build brands, acquire customers, and scale businesses using strategies that are also used by successful entrepreneurs in the industry."
    },
    "mentors": [
      {
        "name": "Brent Corley",
        "bio": "Brent is an experienced facilitator and education leader with over 20 years of experience helping students across Saudi Arabia and the MENA region build real-world skills for the future. With an MBA from the University of Liverpool, he designs hands-on learning experiences focused on entrepreneurship, innovation, and practical problem-solving. During this internship, Brent will work closely with students to turn ideas into real, workable solutions – guiding them through business thinking, teamwork, creativity, and structured execution.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-27-at-4.36.39-PM-1.png"
      },
      {
        "name": "Sanjul Ashok Bhakri",
        "bio": "Sanjul blends expertise across technology, education, and leadership, backed by a strong multidisciplinary academic foundation. With an M.Ed. from Harvard University, an MBA from UCD Michael Smurfit Graduate Business School, and a B.E. in Computer Science from The NorthCap University, Sanjul brings a dynamic perspective to driving innovation, strategic thinking, and impactful, future-focused solutions.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/03/Screen-Shot-2026-03-17-at-10.07.56-AM.png"
      },
      {
        "name": "Eshika Mahajan",
        "bio": "Eshika co-founded Label Kriesha, an Indian ethnic wear label redefining online shopping for semi-formal and formal suits through inclusive sizing and intentional design. Armed with a Product Design (Fashion) diploma from Pearl Academy and a B.A. (Hons) in English from Delhi University, she brings both craft sensibility and brand fluency to her work. Her industry journey spans The Good Glamm Group, Sirona, and Sarita Handa – building expertise in brand strategy, customer experience, and end-to-end manufacturing. Today, she leads growth, content, and brand identity at Label Kriesha, co-built alongside her mother’s two decades of design legacy.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/05/Screen-Shot-2026-05-30-at-4.54.51-PM.png"
      }
    ],
    "skillsTitle": "6 WEEKS. REAL SKILLS. REAL PROGRESS.",
    "skills": [
      {
        "title": "Hands-On Business Building",
        "text": "Validate real ideas, conduct customer interviews, and build prototypes to experience real-world entrepreneurship.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483300.png"
      },
      {
        "title": "Entrepreneurship in Action",
        "text": "Apply concepts from customer discovery, business modeling, product development, and marketing to turn ideas into ventures.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483307.png"
      },
      {
        "title": "Probelm-Solving Skills",
        "text": "Analyze market opportunities and customer needs to develop reasoning, validation, and decision-making skills.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483302.png"
      },
      {
        "title": "Practical Skills Development",
        "text": "Learn customer interviewing, market research, business model design, MVP building, and growth marketing strategies.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      },
      {
        "title": "Shadow Entrepreneurs",
        "text": "Explore careers in entrepreneurship, shadow business leaders, and gain insights into startup workflows.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483308.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Present your complete business idea to a panel and receive an official certificate to strengthen your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      }
    ],
    "journeyTitle": "HOW IT WORKS: YOUR 6-WEEK TRANSFORMATION JOURNEY",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Entrepreneurship and Innovation",
        "text": "Understand what entrepreneurship really is, develop an entrepreneurial mindset, and learn to identify problems worth solving. Discover how WickedGüd spotted the opportunity in healthy instant food and built a brand around “unjunking” comfort food."
      },
      {
        "label": "Week 2",
        "title": "Customer Discovery and Market Validation",
        "text": "Master the art of customer interviews and market research, create detailed customer personas, and analyze how WickedGüd validated demand among health-conscious consumers who refuse to compromise on taste."
      },
      {
        "label": "Week 3",
        "title": "Business Models and Value Proposition",
        "text": "Explore different business model types and revenue streams, use the Business Model Canvas to design how your business will work, and study WickedGüd’s direct-to-consumer strategy that disrupted traditional retail models."
      },
      {
        "label": "Week 4",
        "title": "Product Development and MVP (Minimum Viable Product)",
        "text": "Learn design thinking and rapid prototyping techniques, build your MVP – the simplest version of your product – and see how WickedGüd started with core products before expanding their line."
      },
      {
        "label": "Week 5",
        "title": "Marketing, Launch Strategy, and Growth",
        "text": "Develop go-to-market strategies and learn digital marketing fundamentals. Explore WickedGüd’s bold satirical campaigns with Shilpa Shetty that generated viral attention and challenged billion-dollar brands."
      },
      {
        "label": "Week 6",
        "title": "Reflection and Career Pathways in Entrepreneurship",
        "text": "Perfect your pitch presentation skills, present your complete business idea, and explore career paths in entrepreneurship – from building D2C brands to joining startups and venture capital."
      }
    ],
    "experience": {
      "title": "Discover Entrepreneurship in Action",
      "text": "This is more than an online course – step into the shoes of an entrepreneur and explore real-world applications of business building. Students will engage with real-world scenarios, interactive case studies featuring WickedGüd, and gain practical insights into how entrepreneurs work across ideation, validation, product development, and launch.",
      "points": [
        "Live case studies",
        "Shadow practicing entrepreneurs",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Hands-on practice"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "A Smart Step Towards Your Child’s Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from InteriorCompany",
        "Access to recordings​",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Tanmay Atreyas",
        "tag": "Virtual Entrepreneurship, Leadership and Public Speaking Shadowing Program",
        "text": "The mentor for the course was very well-versed in their field and had a lot of knowledge about the course at hand, especially Tajvir, who let everyone have a chance to speak. Personally, I felt that the public speaking course was the most useful out of all."
      },
      {
        "name": "Bhavin Pinnamaneni",
        "tag": "Virtual Entrepreneurship, Leadership and Public Speaking Shadowing Program",
        "text": "This course was truly informative and a fantastic experience. Our mentor was patient and insightful, which really added to the experience. The course has many real-life applications and will seriously help in real situations."
      },
      {
        "name": "Maria Madraswalla",
        "tag": "Virtual Entrepreneurship, Leadership and Public Speaking Shadowing Program",
        "text": "My mentor was knowledgeable about the topic and very engaging with the students. The content could have been more relevant to entrepreneurship; however, it was still useful."
      },
      {
        "name": "Mayar Alahdal",
        "tag": "Virtual Entrepreneurship, Leadership and Public Speaking Shadowing Program",
        "text": "Tajvir Jhamat was truly inspiring and engaging, making each session something to look forward to. He encouraged us to step out of our comfort zones through challenging activities, public speaking, and problem solving while keeping sessions interactive. His guidance helped me identify my weak points and improve as a speaker and leader. I always left with new ideas, motivation, and confidence."
      },
      {
        "name": "Ethan Dsouza",
        "tag": "Virtual Entrepreneurship, Leadership and Public Speaking Shadowing Program",
        "text": "Tajvir in the entrepreneurship program was great and taught me many new topics while helping me understand them clearly."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "2:00 PM - 4:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "2:00 PM - 4:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "3:00 PM - 5:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior knowledge or experience in business to participate?",
        "a": "Absolutely not! This program is designed specifically for beginners with no prior knowledge of entrepreneurship or business. We start with the fundamentals – basic concepts, problem identification, and how business ideas apply to real-life situations – and build step by step from there. If your child is curious about innovation and enjoys learning, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including collaboration platforms for team activities and business model design tools. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers and entrepreneurs. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual internship every Saturday. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on business-building activities, and ask questions in real time. The live, hands-on format is essential – this is active learning and shadowing in entrepreneurship, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper business questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual learning with collaborative activities. Students work on their own business idea or venture concept while also participating in team exercises and group discussions. This mirrors how real entrepreneurial teams collaborate in professional settings."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete an entrepreneurship-focused business project that demonstrates real analytical, creative, and critical thinking skills. The certificate issued reflects their initiative, hands-on learning, and exposure to practical business concepts."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate upon successful completion of the internship. Many use this strong foundation to launch school entrepreneurship clubs, participate in business plan competitions, enter startup pitch contests, or take advanced business coursework. After the internship, students also have the opportunity to enroll in A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey in entrepreneurship and related fields."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "marketing-and-brand-strategy-virtual-internship",
    "industry": "business-marketing",
    "title": "Marketing & Brand Strategy Virtual Internship",
    "metaTitle": "Marketing & Brand Strategy Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Learn how brands grow by creating campaigns, using AI tools, and analyzing audiences while building skills in storytelling… Live online, ages 13-18.",
    "summary": "Learn how brands grow by creating campaigns, using AI tools, and analyzing audiences while building skills in storytelling, content creation, digital strategy, and data-driven marketing decision-making.",
    "subtitle": "Discover How Marketing Works: Explore Branding and Digital Campaigns",
    "lead": "Experiential 6-Week Virtual Internship for Ages 13–18",
    "promise": "From Zero Experience to Marketing Pro",
    "perks": [
      "No marketing background needed",
      "Live sessions every Saturday",
      "Shadow marketers and brand strategists"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=247",
    "sourceUrl": "https://alearninglab.com/marketing-and-brand-strategy-shadowing-program/",
    "partnership": {
      "logo": "https://alearninglab.com/wp-content/uploads/2026/01/tybblkh7dzbcwxbvhlsf-1.png",
      "text": "Squareyards brings real-world marketing innovation into your learning experience. Our industry partner provides hands-on exposure to how professional campaigns are created using AI-powered tools and data-driven strategies that reach millions. Through live case studies, practical examples, and behind-the-scenes insights, students explore how marketers build brands, create content, segment audiences, and launch campaigns using technologies and methods that are also used by professionals in the industry."
    },
    "mentors": [
      {
        "name": "Sumeet Ambegaokar",
        "bio": "A seasoned marketing and business leader with over 30 years of experience across education, healthcare, retail, telecom, and FMCG. An MBA in Marketing and a Mechanical Engineer by education, he combines strategic thinking with operational excellence. Sumeet has led large-scale brand transformations, digital marketing, admissions growth, corporate partnerships, and customer experience initiatives across regional and international organizations. Recognized globally for leadership and brand-building excellence, his strengths lie in strategic planning, team leadership, performance-driven marketing, and building high-impact partnerships.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/Frame-1707483384.jpg"
      },
      {
        "name": "Avani Jain",
        "bio": "Avani works as the Head of Visual Communications, holding a Bachelor’s degree in Animation and a strong foundation in visual storytelling. With extensive experience across creative design, video production, and brand communication, she leads end-to-end creative processes and develops visuals that connect with students and families. Her strengths include structured creative thinking, attention to detail, and translating ideas into clear, impactful content. She enjoys balancing creativity with consistency to ensure every output aligns with the brand’s values and voice. Leading creative initiatives and seeing their real-world impact continues to be both fulfilling and motivating for her.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/04/Frame-1707483383.jpg"
      },
      {
        "name": "Ahmed Saqlain",
        "bio": "Ahmed is a seasoned marketing leader with over a decade of experience spanning creative agencies, telecom giants, and international development. Currently serving as Vice President Brands at Mobilink Bank, his career trail runs through some of Pakistan’s most influential organisations – Adcom Leo Burnett, Zong, Jazz, Telenor, and the British Council – giving him a rare cross-industry lens on brand building. Armed with an MBA in Marketing from NUST, Ahmed brings both strategic rigour and creative instinct to every brand challenge.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/06/Screen-Shot-2026-06-03-at-2.17.58-PM.png"
      },
      {
        "name": "Farhan Khan",
        "bio": "Farhan is a growth-focused marketing professional with over four years of experience spanning social media strategy, brand positioning, and integrated campaign management. Having worked across BLKBOX.ai, Cinemon Consulting, and Carbon Mandal, he has led end-to-end brand identity projects for corporates, MSMEs, and startups – managing everything from influencer marketing and content calendars to performance analytics and creative team leadership. Armed with a B.Com (Hons) in Marketing & Finance and proficiency across tools including Meta Business Suite, HubSpot, Adobe Suite, and Canva, Farhan brings both strategic rigour and hands-on creative execution to the world of modern brand building.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Farhan.png"
      }
    ],
    "skillsTitle": "6 WEEKS. REAL SKILLS. REAL PROGRESS.",
    "skills": [
      {
        "title": "Hands-On Marketing Practice",
        "text": "Design brand strategies, create social media campaigns, and develop content to experience real-world marketing and brand building.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483300.png"
      },
      {
        "title": "Marketing in Action",
        "text": "Apply concepts from brand identity, content creation, digital campaigns, and data analytics to reach and engage audiences effectively.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483307.png"
      },
      {
        "title": "Critical Thinking Skills",
        "text": "Analyze customer needs and market opportunities to develop reasoning, strategic thinking, and decision-making skills.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483302.png"
      },
      {
        "title": "Practical Skills Development",
        "text": "Learn AI tools, audience segmentation, brand design, content marketing, SEO basics, and social media strategy.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      },
      {
        "title": "Shadow Professionals",
        "text": "Explore careers in marketing, shadow brand strategists, and gain insights into campaign workflows.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483308.png"
      },
      {
        "title": "Earn a Certificate",
        "text": "Present your complete marketing campaign and receive an official certificate to strengthen your profile.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483301.png"
      }
    ],
    "journeyTitle": "HOW IT WORKS: YOUR 6-WEEK TRANSFORMATION JOURNEY",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Introduction to Marketing and Branding",
        "text": "Learn what marketing really is, explore the marketing mix (product, price, place, promotion), understand brand positioning, and discover how Squareyards uses AI to revolutionize real estate marketing at scale."
      },
      {
        "label": "Week 2",
        "title": "Know Your Customer – Research and Personas",
        "text": "Master market research techniques and create detailed customer personas based on data, analyze customer behavior patterns, and study how Squareyards segments millions of property buyers for personalized campaigns."
      },
      {
        "label": "Week 3",
        "title": "Build a Brand Identity",
        "text": "Design logos and choose brand colors, create complete brand guidelines including typography and visual style, and explore Squareyards’ AI tools that enhance property images and create virtual staging."
      },
      {
        "label": "Week 4",
        "title": "Master Content and Storytelling",
        "text": "Learn content marketing fundamentals and SEO basics, craft compelling brand stories that resonate with audiences, and explore Squareyards’ PropVR platform that transforms floor plans into immersive 3D marketing videos."
      },
      {
        "label": "Week 5",
        "title": "Launch Digital Campaigns",
        "text": "Develop social media strategies across Instagram, TikTok, and LinkedIn, understand platform algorithms and analytics, learn what makes content go viral, and create data-driven campaign strategies."
      },
      {
        "label": "Week 6",
        "title": "Reflection and Career Pathways in Marketing",
        "text": "Present your complete marketing campaign professionally, build your portfolio with campaign work and brand designs, and explore career paths in marketing, branding, content creation, and digital strategy."
      }
    ],
    "experience": {
      "title": "Discover Marketing in Action",
      "text": "This is more than an online course – step into the shoes of a marketing professional and explore real-world applications of branding and campaigns. Students will engage with real-world scenarios, interactive case studies featuring Squareyards, and gain practical insights into how marketers work across brand strategy, content creation, AI tools, and digital campaigns.",
      "points": [
        "Live case studies",
        "Shadow practicing entrepreneurs",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Hands-on practice"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "A Smart Step Towards Your Child’s Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from InteriorCompany",
        "Access to recordings​",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Ashmik Mehngi",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really liked the Virtual Engineering Shadowing program and thought it was a great way to learn. Our mentor was very knowledgeable and always made the sessions interesting, which made it easy to understand even the most difficult topics. She gave us useful information about different areas of engineering and encouraged us to think critically and ask questions. I especially enjoyed the practical parts of the course, like coding and CAD design. Her friendly and encouraging teaching style made a big difference, and the course gave me both knowledge and confidence to explore engineering further."
      },
      {
        "name": "Mohamed Siddig Mohyeldin Siddig",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "I really enjoyed the course in the engineer shadowing program. Our mentor had a very thorough teaching style that helped me clarify my understanding of the different engineering fields. The fun activities and tools made it easier to understand and apply what we learned."
      },
      {
        "name": "Leyl Bilgrami",
        "tag": "Virtual AI, Programming & Computer Science Shadowing",
        "text": "Our mentor was extremely clear and shared a lot of valuable information about the course. He was kind, engaging, and made me more interested in AI."
      },
      {
        "name": "Humayl Fazal",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The 6-week virtual job shadowing program gave me exposure to various engineering fields—from car design and robotics to sustainability and aerodynamics—while helping me gain industry insights and clarity on future career pathways."
      },
      {
        "name": "Syed Mahad Ahmed",
        "tag": "Virtual Engineer Shadowing Program",
        "text": "The engineering shadowing internship was really helpful. My mentor explained concepts clearly and gave me advice that made me more confident about engineering. Learning how engineering works in real life has made me feel more confident about my future."
      }
    ],
    "batches": [
      {
        "when": "12 till 17 October, 2026 (Monday to Saturday)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "10:00 AM - 12:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "11:00 AM - 1:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any prior knowledge or experience in marketing to participate?",
        "a": "Absolutely not! This program is designed specifically for beginners with no prior knowledge of marketing or branding. We start with the fundamentals – basic marketing concepts, customer research, and how brand strategies apply to real-life campaigns – and build step by step from there. If your child is curious about how brands reach audiences and enjoys learning, they’re ready to begin."
      },
      {
        "q": "What age range is this program designed for?",
        "a": "The program is optimized for ages 13-18 (typically high school students). The content complexity, pacing, and career discussions are tailored for this developmental stage. Students younger than 13 may find the material challenging, while students 18+ might prefer our more advanced offerings."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with reliable internet access and a webcam for interaction. All tools we use are free and browser-based, including AI marketing platforms, design tools, and collaboration platforms for team activities. We’ll send detailed setup instructions one week before the program begins."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers and marketing professionals. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Is this actually live instruction or pre-recorded videos?",
        "a": "100% live virtual internship every Saturday. Your child will interact directly with instructors, participate in team breakout rooms, collaborate on campaign activities, and ask questions in real time. The live, hands-on format is essential – this is active learning and shadowing in marketing, not passive video watching."
      },
      {
        "q": "How do you accommodate different learning paces?",
        "a": "We keep cohorts small (maximum 20 students) to provide differentiated support. Advanced students receive bonus challenges and deeper marketing questions, while beginners get additional guidance and scaffolding. Our instructors are experienced at meeting students where they are and ensuring everyone progresses confidently."
      },
      {
        "q": "Will students work individually or in teams?",
        "a": "Both! The program balances individual learning with collaborative activities. Students work on their own marketing campaign or brand project while also participating in team exercises and group discussions. This mirrors how real marketing teams collaborate in professional settings."
      },
      {
        "q": "Can parents observe the sessions?",
        "a": "Parents are welcome to observe the first 15 minutes of Week 1 to see the format and teaching style. After that, we find students engage more authentically and ask better questions when participating independently. You’ll have full access to session recordings and regular progress updates throughout the program."
      },
      {
        "q": "Will this actually help with college applications?",
        "a": "Definitely. Students complete a marketing-focused campaign project that demonstrates real creative, strategic, and analytical thinking skills. The certificate issued reflects their initiative, hands-on learning, and exposure to practical marketing concepts."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate upon successful completion of the internship. Many use this strong foundation to join school marketing clubs, participate in business competitions, launch personal brand projects, or take advanced marketing coursework. After the internship, students also have the opportunity to enroll in A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey in marketing and related fields."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  },
  {
    "slug": "lawyer-virtual-internship",
    "industry": "law-public-policy-global-affairs",
    "title": "Lawyer Virtual Internship",
    "metaTitle": "Lawyer Virtual Internship for Ages 13-18 | A Learning Lab India",
    "metaDescription": "Step into the legal world by analyzing real cases, building arguments, and participating in mock trials while developing skills… Live online, ages 13-18.",
    "summary": "Step into the legal world by analyzing real cases, building arguments, and participating in mock trials while developing skills in legal reasoning, advocacy, critical thinking, and understanding modern legal systems.",
    "subtitle": "Law Virtual Internship Program",
    "lead": "Experience Law From the Inside in 6 weeks | For Ages 13 – 18",
    "promise": "From Courtroom Observer to Confident Advocate",
    "perks": [
      "No prior legal knowledge needed",
      "6 weeks of live instruction",
      "Real case studies from Jodha Legal"
    ],
    "duration": "6 Weeks",
    "sessions": 6,
    "ages": "13-18",
    "maxStudents": 10,
    "fee": {
      "amount": 300,
      "currency": "USD"
    },
    "enrolUrl": "https://alearninglab.com/payments?program_id=246",
    "sourceUrl": "https://alearninglab.com/virtual-internships/law-virtual-shadowing-program/",
    "partnership": {
      "logo": "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Black-on-Transparent.png",
      "text": "This internship features an exclusive case study partnership with Jodha Legal, an innovative legal consultancy redefining how growing businesses access top-tier legal advice."
    },
    "mentors": [
      {
        "name": "Divya Rajora",
        "bio": "Divya Rajora is a penultimate-year law student at the University of Birmingham, UK, aspiring to qualify as a solicitor. She has a strong foundation in legal research and core legal principles, with growing exposure to the UK legal education system. Divya is keen to support students navigating academic decisions and early pathways into studying and pursuing law in the UK.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/image-36.png"
      },
      {
        "name": "Lama Gibreel",
        "bio": "Lama Gibreel is a legal professional with an LLB (Hons) from Middlesex University, currently serving as Consultant and Contract Administrator at DAMAC Properties – one of the region’s most prominent real estate developers. In her role, she reviews, drafts, and administers contracts, supports negotiations, and ensures contractual compliance across large-scale projects, giving her hands-on command of commercial law in a fast-paced business environment. Lama brings to the Law Virtual Internship a practitioner’s clarity on how legal knowledge translates into real-world decision-making – and a genuine passion for helping aspiring lawyers understand the intersection of law, business, and contract management.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/mentor-law.jpg"
      },
      {
        "name": "Sarfaraaz Ganie",
        "bio": "Sarfaraaz Ganie is a legally trained academic with a Bachelor’s degree in Law and a Master’s from the University of Stellenbosch, South Africa. He has worked across academic and legal environments including Stellenbosch University, SDM (In-house Legal), Eduvos, and Red & Yellow. Alongside his academic and legal experience, he has contributed to projects associated with globally recognised organisations and multidisciplinary teams acorss UK, US, and Middle East, bringing a strong practical understanding of legal reasoning, regulatory interpretation, and real-world application.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/02/Sarfaraaz-Law-VI.jpeg"
      },
      {
        "name": "Dhruv Ganesh",
        "bio": "Dhruv is a 2:1 LLB graduate from the Durham University who has successfully completed both Solicitors Qualifying Examination (SQE1) and Solicitors Qualifying Examination (SQE2). He has gained cross-jurisdictional legal internship experience across the United Arab Emirates and India, bringing practical international exposure to aspiring law students.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Frame-1707483388.png"
      },
      {
        "name": "Sarah Fauzna",
        "bio": "Sarah is a licensed advocate with a dual background in Business and Law, bringing both corporate strategic thinking and legal precision to her practice. Her internship experience at global firms Baker McKenzie and Habib Al Mulla has sharpened her expertise in rigorous legal research, contract drafting, client advisory, and regulatory compliance – building a strong command of commercial law in high-stakes, real-world environments. Sarah brings to the Internship a practitioner’s rigour and a genuine commitment to helping aspiring legal minds understand how advocacy, integrity, and strategy work together in the modern legal landscape.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/07/Frame-1707483389.png"
      }
    ],
    "skillsTitle": "6 Weeks. Real Skills. Real Progress.",
    "skills": [
      {
        "title": "Argue Like a Barrister",
        "text": "Present legal arguments in a real mock trial with no prior experience needed.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483311.png"
      },
      {
        "title": "Analyze Real Legal Cases",
        "text": "Work with real case studies to see how lawyers solve business problems.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483312.png"
      },
      {
        "title": "Master Legal Reasoning",
        "text": "Learn to identify legal issues, apply law to facts, and build persuasive arguments.",
        "image": "https://alearninglab.com/wp-content/uploads/2025/12/Frame-1707483293.png"
      },
      {
        "title": "Develop Courtroom Confidence",
        "text": "Receive personalized feedback on your advocacy.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483313.png"
      },
      {
        "title": "Understand Modern Legal Practice",
        "text": "Learn from practicing lawyers about commercial disputes.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483314.png"
      },
      {
        "title": "Earn a Certificate Upon Completion",
        "text": "Official completion certificate to strengthen university applications.",
        "image": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483315.png"
      }
    ],
    "journeyTitle": "How It Works: Your 6-Week Transformation",
    "weeks": [
      {
        "label": "Week 1",
        "title": "Intro to Law and Legal Systems",
        "text": "Understand how the UK legal system works, explore different legal careers, and analyze your first real business case from Jodha Legal."
      },
      {
        "label": "Week 2",
        "title": "Learn Legal Advocacy",
        "text": "Master mooting and case analysis. Examine a real commercial dispute and learn how lawyers identify legal issues and build arguments."
      },
      {
        "label": "Week 3",
        "title": "Master Criminal Law",
        "text": "See how criminal law principles apply to business compliance through a real regulatory case."
      },
      {
        "label": "Week 4",
        "title": "Argue in Court",
        "text": "Present your case as a barrister in a live mock trial. Respond to judges’ questions and receive personalized feedback on your advocacy."
      },
      {
        "label": "Week 5",
        "title": "Navigate Legal Ethics",
        "text": "Debate real ethical dilemmas from practice. Should AI practice law? How do you defend someone you believe is guilty? You decide."
      },
      {
        "label": "Week 6",
        "title": "Plan Your Legal Career",
        "text": "Learn education pathways, hear from a Jodha Legal lawyer about their journey, and receive your certificate of completion."
      }
    ],
    "experience": {
      "title": "Learn From A Real Company, Not Just Textbooks",
      "text": "Your child won’t just learn theory, they’ll see exactly how professional lawyers solve real cases.",
      "points": [
        "Live Presentation",
        "Actual Projects",
        "Guest speaker Q&A",
        "Behind-the-scenes insights",
        "Career guidance",
        "Real-world context"
      ]
    },
    "why": {
      "intro": "Immersive Virtual Internship That Brings Real-World Learnings to Life.",
      "items": [
        {
          "title": "Small Class Sizes",
          "text": "Students gets individual attention and personalized feedback."
        },
        {
          "title": "Experienced Instructors",
          "text": "Industry professionals who've worked at leading tech companies."
        },
        {
          "title": "Safe & Supportive",
          "text": "Monitored environment. We focus on building confidence, not just skills."
        }
      ]
    },
    "certificate": {
      "text": "See what our participants receive after successfully completing the program. Each certificate recognizes your participation, skills learned, and achievements, and can be shared digitally or printed for your portfolio.",
      "image": "https://alearninglab.com/wp-content/uploads/2026/01/Certificate-Sample-1024x724.png"
    },
    "valueProps": {
      "title": "Invest in Your Child's Legal Future",
      "items": [
        "12 hours of live instruction",
        "Case studies from Jodha Legal",
        "Access to recordings",
        "Portfolio-worthy analysis project"
      ]
    },
    "testimonials": [
      {
        "name": "Niti Newalkar",
        "tag": "Virtual Lawyer Shadowing Program",
        "text": "I took part in the Virtual Lawyer Shadowing Program over 6 weeks, and it was an extremely valuable and educational experience. My mentors, Maryam and Sam, were brilliant at both making the content fun and engaging, and helping me learn more about the path I want to pursue. The course went beyond just university applications, even helping me think about future job opportunities and career options. Overall, I would highly recommend this experience to anyone interested in the legal field—it is truly worth it!"
      },
      {
        "name": "Shreyan Chakraborty",
        "tag": "Virtual Lawyer Shadowing Program",
        "text": "Over the course of the internship we had two mentors, Enrico and Sam, who were great at teaching and made the content very interesting and the sessions interactive. We discussed interesting topics and the highlight was the mock trials that they conducted, which were a great experience. They also explained complex topics in a very digestible way."
      },
      {
        "name": "Navneet Nandakumar",
        "tag": "Virtual Lawyer Shadowing Program",
        "text": "The teaching and the course was very insightful, and it certainly helped me understand more about the gist of law and legal careers and opportunities. I would definitely recommend this program to a friend or family member."
      },
      {
        "name": "Aayat Bella",
        "tag": "Virtual Lawyer Shadowing Program",
        "text": "My mentor made things very easy to understand and incorporated a good mix of independent and taught work. This course helped me realize what I needed to work on, and my mentor supported me in improving those skills throughout the six weeks."
      },
      {
        "name": "Ahmed Mutahar",
        "tag": "Virtual Lawyer Shadowing Program",
        "text": "I really enjoyed the summer law course—it was engaging, informative, and well-organized. The presenter was not only knowledgeable but also a genuinely kind and supportive mentor. It was a great experience overall!"
      }
    ],
    "batches": [
      {
        "when": "12 till 17th October, 2026 (Monday till Saturday)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-10-12"
      },
      {
        "when": "18 till 22 October, 2026 (Sundays till Thursday)",
        "time": "3:00 PM - 5:00 PM AST",
        "start": "2026-10-18"
      },
      {
        "when": "25 till 29 October, 2026 (Sundays till Thursday)",
        "time": "3:00 PM - 5:00 PM AST",
        "start": "2026-10-25"
      },
      {
        "when": "7 November - 12 December, 2026 (Saturdays)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-11-07"
      },
      {
        "when": "19 December - 3 January, 2027 (Saturdays & Sundays)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2026-12-19"
      },
      {
        "when": "9 January - 13 February, 2027 (Saturdays)",
        "time": "4:00 PM - 6:00 PM GST",
        "start": "2027-01-09"
      }
    ],
    "faqs": [
      {
        "q": "Does my child need any legal knowledge or experience?",
        "a": "Absolutely not! This program is designed for complete beginners. We start with fundamentals – what courts do, how the legal system works, basic terminology – and build systematically. If your child is curious about law, can think critically, and is willing to speak up in discussions, they’re ready. No prior legal study required."
      },
      {
        "q": "What equipment or software do they need?",
        "a": "Just a computer (Mac or Windows) with stable internet connection, webcam, and microphone. All case materials are provided digitally. We use standard video conferencing platforms – no special software purchases needed. We’ll send technical setup instructions one week before the program starts."
      },
      {
        "q": "Is this program suitable for both younger (13-14) and older (17-18) children?",
        "a": "Yes. We design activities to engage the full age range. The case materials and legal concepts are appropriately challenging for all ages 13-18. Younger students benefit from structured guidance and skill development, while older students can dive deeper into university preparation and career planning. Mixed-age cohorts also create valuable peer learning opportunities."
      },
      {
        "q": "What if they miss a session?",
        "a": "We do not encourage students to miss any classes, as live sessions are essential for active participation, real-time discussions, team activities, and interaction with guest speakers. However, in the event of an unavoidable emergency, all sessions are recorded in high quality and made available within 24 hours via the student dashboard, allowing students to review the missed content at their convenience."
      },
      {
        "q": "Do students have to present arguments in front of others?",
        "a": "Yes – that’s core to the experience. Week 4’s mock trial involves formal presentations where students act as barristers presenting arguments. We understand public speaking is challenging, which is why we build gradually: Week 2 introduces mooting concepts, Week 3 focuses on preparation and practice, then Week 4 is the full courtroom experience. Everyone presents, everyone receives supportive feedback, and everyone grows. Students consistently report this as the most valuable part."
      },
      {
        "q": "What legal areas does the program cover?",
        "a": "The curriculum focuses on criminal law through the R v. Thompson case (murder, causation, self-defense, intent), but we explore broader legal principles applicable across all practice areas. Week 1 introduces multiple fields: family law, commercial law, intellectual property, human rights, employment law. Week 5 covers contemporary issues like AI in legal practice and access to justice. The skills you develop – legal analysis, argumentation, advocacy – transfer to any legal specialty."
      },
      {
        "q": "Are the sessions really live and interactive?",
        "a": "Yes, 100% live every Saturday. Students participate in small group breakout rooms for case analysis, present arguments to the full class, respond to questions from instructors acting as judges, and engage in ethical debates. This isn’t watching pre-recorded lectures – it’s active legal practice in real-time."
      },
      {
        "q": "How does the guest speaker session work?",
        "a": "In Week 6, a practicing solicitor or barrister joins live to share their career journey, discuss day-to-day legal practice, and answer student questions. Students can ask about anything: education pathways, training contract applications, work-life balance, interesting cases, challenges in practice. It’s an invaluable opportunity to hear directly from someone working in law right now."
      },
      {
        "q": "Will this actually help with university law applications?",
        "a": "Yes, significantly. Admissions tutors want evidence of genuine legal interest beyond watching courtroom dramas. This program provides concrete experiences to discuss: “I analyzed R v. Thompson in a mooting exercise where I argued issues of causation and intent before a mock Court of Appeal.” You’ll also develop advocacy skills, legal analysis ability, and understanding of professional ethics – exactly what law schools look for."
      },
      {
        "q": "What happens when the internship is over?",
        "a": "Students receive a certificate of completion documenting their legal training, lifetime access to all session recordings and case materials, and resources for continued learning (recommended reading, mooting competitions, work experience opportunities). Many students use this foundation to pursue school debate clubs, enter mooting competitions, seek legal work experience, or simply make more informed decisions about legal careers. Post-internship, students also have the opportunity to enroll in any of A Learning Lab’s specialized programs and can avail a complimentary 30-minute consultation with our experts to thoughtfully plan their academic and career journey ahead."
      },
      {
        "q": "What's your refund policy?",
        "a": "Please note that we do not offer refunds under any circumstances. However, in the event of an unavoidable emergency that prevents you from attending the internship, we request that you inform us in advance. In such cases, you may choose to transfer the fee paid toward any other A Learning Lab program of your choice, allowing you to continue your learning journey without losing the value of your enrollment."
      }
    ]
  }
];

const BY_SLUG = Object.fromEntries(PROGRAM_DETAILS.map((p) => [p.slug, p]));

export function getProgramDetail(slug) {
  return BY_SLUG[slug] || null;
}

/**
 * Cards where the programme's own first illustration is shared with another programme
 * (entrepreneurship and marketing reuse the AI & CS set on the old site).
 */
const CARD_IMAGE_OVERRIDES = {
  "entrepreneurship-and-innovation-virtual-internship": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483314.png",
  "marketing-and-brand-strategy-virtual-internship": "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483322-1.png",
  "ai-and-computer-science-virtual-internship":
    "https://alearninglab.com/wp-content/uploads/2026/01/Frame-1707483314.png",
  "engineering-virtual-internship":
    "https://d23ed2vwswjjj7.cloudfront.net/all/2026/01/Frame-1707483332.png",
};

/** Live programme pages on alearninglab.com for slugs not yet in PROGRAM_DETAILS. */
const SOURCE_URL_OVERRIDES = {
  "ai-and-computer-science-virtual-internship":
    "https://alearninglab.com/virtual-internships/ai-computer-science-shadowing/",
  "engineering-virtual-internship": "https://alearninglab.com/engineering-virtual-shadowing-program/",
};

/** Canonical alearninglab.com URL for a programme slug (industry cards, etc.). */
export function getProgramSourceUrl(slug) {
  if (SOURCE_URL_OVERRIDES[slug]) return SOURCE_URL_OVERRIDES[slug];
  return BY_SLUG[slug]?.sourceUrl ?? null;
}

/** Card thumbnail for programme grids — assets from the live alearninglab.com programme pages. */
export function getProgramCardImage(slug) {
  if (CARD_IMAGE_OVERRIDES[slug]) return CARD_IMAGE_OVERRIDES[slug];
  const program = BY_SLUG[slug];
  if (!program) return null;
  if (program.cardImage) return program.cardImage;

  const skillImage = program.skills?.find(
    (s) => s.image && !/Certificate-Sample/i.test(s.image),
  )?.image;
  if (skillImage) return skillImage;

  const mentorImage = program.mentors?.find((m) => m.image)?.image;
  return mentorImage || null;
}

/** One of a programme's skill illustrations (0-based), used to give related programmes distinct thumbnails. */
export function getProgramSkillImage(slug, index = 0) {
  const skills = BY_SLUG[slug]?.skills ?? [];
  const usable = skills.filter((s) => s.image && !/Certificate-Sample/i.test(s.image));
  return usable[index]?.image ?? usable[0]?.image ?? null;
}

export function programPath(slug) {
  return `/programs/${slug}`;
}
